import "./styles.css";
import {
  cellIndex, cloneMap, createInitialMap, deserializeMap, IMAGE_MAX_SCALE, IMAGE_MIN_SCALE, moveImage,
  moveProp, paintGround, placeImage, placeProp, removeImage, serializeMap, setTileElevation, updateImageTransform,
  type BrightnessCorrection, type GroundType, type MapDocument, type MapImagePlacement, type PropType, type TileElevation,
} from "./editor-model";
import {
  getBridgeConnectionDirections,
  getBridgeConnectionShape,
  getBridgeEndpointMask,
  getBridgeTextureRotation,
  getPropNeighborMask,
  getTransitionCorrections,
  getWaterBankCornerMask,
  getWaterBankMask,
  getWaterDepths,
  NEIGHBOR_MASK,
  type BridgeDirection,
} from "./autotile";
import {
  AdminClient, AuthApiError, AuthClient, isAvatarIcon, parsePublicAppConfig,
  type AdminMapSummary, type AdminUser, type AuthSession, type AvatarIcon,
} from "./auth-client";
import { ImageLibraryClient, ImageLibraryError, isImageUploadOversized, prepareImageForUpload, type ImageAsset } from "./image-library";
import { getResizeOffsets, MAX_MAP_SIZE, MIN_MAP_SIZE, MapStorageClient, MapStorageError, resizeMap, type ResizeAnchor } from "./map-library";
import { formatDeploymentRelativeTime, formatDeploymentTime, parseDeploymentMetadata } from "./deployment-meta";

const CELL_SIZE = 36;
const MAX_BRUSH_SIZE = 10;
const DEFAULT_IMAGE_ROTATION = 0;
const DEFAULT_IMAGE_SCALE = 2;
const STORAGE_KEY = "mapeditor-draft-v1";
const AUTH_STORAGE_KEY = "mapeditor-auth-v3";
const LEGACY_AUTH_STORAGE_KEY = "mapeditor-auth-v2";
const isImageLibraryPage = /^\/images\/?$/u.test(window.location.pathname);
const DEFAULT_DISPLAY_NAME = "새유저";
const avatarOptions: Array<{ id: AvatarIcon; label: string; glyph: string }> = [
  { id: "initial", label: "글자", glyph: "가" },
  { id: "hidden", label: "숨김", glyph: "—" },
  { id: "leaf", label: "나뭇잎", glyph: "🌿" },
  { id: "pine", label: "소나무", glyph: "🌲" },
  { id: "water", label: "물방울", glyph: "💧" },
  { id: "stone", label: "바위", glyph: "🪨" },
];
const groundOptions: Array<{ id: GroundType; label: string; hint: string }> = [
  { id: "grass", label: "풀", hint: "부드러운 초지" },
  { id: "dirt", label: "흙", hint: "산책로와 둔덕" },
  { id: "stone", label: "돌", hint: "바위와 물가" },
  { id: "water", label: "물", hint: "개울과 연못" },
];
const propOptions: Array<{ id: PropType; label: string; image: string }> = [
  { id: "broadleaf-tree", label: "활엽수", image: "/assets/props/broadleaf-tree.png" },
  { id: "pine-tree", label: "소나무", image: "/assets/props/pine-tree.png" },
  { id: "shrub", label: "관목", image: "/assets/props/shrub.png" },
  { id: "boulder", label: "바위", image: "/assets/props/boulder.png" },
  { id: "fallen-log", label: "쓰러진 통나무", image: "/assets/props/fallen-log.png" },
  { id: "footbridge", label: "나무다리", image: "/assets/props/footbridge.png" },
];
type ImageMaterialTab = "default" | "mine";
const defaultImageAssets: readonly ImageAsset[] = [
  { id: "builtin:rune-shrine", originalFilename: "룬 석비", originalUrl: "/assets/landmarks/rune-shrine.png", thumbnailUrl: "/assets/landmarks/rune-shrine.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:stone-archway", originalFilename: "이끼 낀 석조 아치", originalUrl: "/assets/landmarks/stone-archway.png", thumbnailUrl: "/assets/landmarks/stone-archway.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:wayfinder-post", originalFilename: "숲길 방향 표지", originalUrl: "/assets/landmarks/wayfinder-post.png", thumbnailUrl: "/assets/landmarks/wayfinder-post.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:ancient-well", originalFilename: "고대 우물", originalUrl: "/assets/landmarks/ancient-well.png", thumbnailUrl: "/assets/landmarks/ancient-well.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:fairy-lantern", originalFilename: "요정 랜턴", originalUrl: "/assets/landmarks/fairy-lantern.png", thumbnailUrl: "/assets/landmarks/fairy-lantern.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:moon-sundial", originalFilename: "달빛 해시계", originalUrl: "/assets/landmarks/moon-sundial.png", thumbnailUrl: "/assets/landmarks/moon-sundial.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:owl-totem", originalFilename: "부엉이 토템", originalUrl: "/assets/landmarks/owl-totem.png", thumbnailUrl: "/assets/landmarks/owl-totem.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:forest-watchtower", originalFilename: "숲 감시탑", originalUrl: "/assets/landmarks/forest-watchtower.png", thumbnailUrl: "/assets/landmarks/forest-watchtower.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:standing-stone-ring", originalFilename: "선돌 고리", originalUrl: "/assets/landmarks/standing-stone-ring.png", thumbnailUrl: "/assets/landmarks/standing-stone-ring.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:dryad-statue", originalFilename: "드라이어드 석상", originalUrl: "/assets/landmarks/dryad-statue.png", thumbnailUrl: "/assets/landmarks/dryad-statue.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:shrine-altar", originalFilename: "숲의 제단", originalUrl: "/assets/landmarks/shrine-altar.png", thumbnailUrl: "/assets/landmarks/shrine-altar.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:stump-throne", originalFilename: "고목 왕좌", originalUrl: "/assets/landmarks/stump-throne.png", thumbnailUrl: "/assets/landmarks/stump-throne.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:crystal-cluster", originalFilename: "청록 수정 군락", originalUrl: "/assets/landmarks/crystal-cluster.png", thumbnailUrl: "/assets/landmarks/crystal-cluster.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:boundary-bell", originalFilename: "숲 경계 종", originalUrl: "/assets/landmarks/boundary-bell.png", thumbnailUrl: "/assets/landmarks/boundary-bell.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:stair-marker", originalFilename: "나선 계단 표식", originalUrl: "/assets/landmarks/stair-marker.png", thumbnailUrl: "/assets/landmarks/stair-marker.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:ranger-board", originalFilename: "순찰 게시판", originalUrl: "/assets/landmarks/ranger-board.png", thumbnailUrl: "/assets/landmarks/ranger-board.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:meditation-pavilion", originalFilename: "명상 정자", originalUrl: "/assets/landmarks/meditation-pavilion.png", thumbnailUrl: "/assets/landmarks/meditation-pavilion.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:sealed-door", originalFilename: "봉인된 석문", originalUrl: "/assets/landmarks/sealed-door.png", thumbnailUrl: "/assets/landmarks/sealed-door.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:antler-shrine", originalFilename: "뿔 장식 제단", originalUrl: "/assets/landmarks/antler-shrine.png", thumbnailUrl: "/assets/landmarks/antler-shrine.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:guardian-mask", originalFilename: "숲 수호자 가면", originalUrl: "/assets/landmarks/guardian-mask.png", thumbnailUrl: "/assets/landmarks/guardian-mask.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:waterfall", originalFilename: "숲 폭포", originalUrl: "/assets/landmarks/waterfall.png", thumbnailUrl: "/assets/landmarks/waterfall.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:waterfall-pool", originalFilename: "폭포 웅덩이", originalUrl: "/assets/landmarks/waterfall-pool.png", thumbnailUrl: "/assets/landmarks/waterfall-pool.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:forest-spring", originalFilename: "꽃잎 옹달샘", originalUrl: "/assets/landmarks/forest-spring.png", thumbnailUrl: "/assets/landmarks/forest-spring.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:suspension-bridge-diagonal-ne", originalFilename: "흔들다리 ↗", originalUrl: "/assets/landmarks/suspension-bridge-diagonal-ne.png", thumbnailUrl: "/assets/landmarks/suspension-bridge-diagonal-ne.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:suspension-bridge-diagonal-nw", originalFilename: "흔들다리 ↖", originalUrl: "/assets/landmarks/suspension-bridge-diagonal-nw.png", thumbnailUrl: "/assets/landmarks/suspension-bridge-diagonal-nw.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:shelter-hatch", originalFilename: "쉘터 해치 입구", originalUrl: "/assets/landmarks/shelter-hatch.png", thumbnailUrl: "/assets/landmarks/shelter-hatch.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:supply-warehouse", originalFilename: "보급 창고", originalUrl: "/assets/landmarks/supply-warehouse.png", thumbnailUrl: "/assets/landmarks/supply-warehouse.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
  { id: "builtin:barred-gate", originalFilename: "차단봉 문", originalUrl: "/assets/landmarks/barred-gate.png", thumbnailUrl: "/assets/landmarks/barred-gate.png", mimeType: "image/png", byteSize: 1, createdAt: "static" },
];
// Keep the former built-in ids resolvable for old drafts/maps, but do not expose
// them in the new default landmark palette.
const legacyBuiltInImageAssets: readonly ImageAsset[] = [
  { id: "builtin:broadleaf-tree", originalFilename: "넓은잎 나무", originalUrl: "/assets/props/broadleaf-tree.png", thumbnailUrl: "/assets/props/broadleaf-tree.png", mimeType: "image/png", byteSize: 1, createdAt: "legacy" },
  { id: "builtin:pine-tree", originalFilename: "소나무", originalUrl: "/assets/props/pine-tree.png", thumbnailUrl: "/assets/props/pine-tree.png", mimeType: "image/png", byteSize: 1, createdAt: "legacy" },
  { id: "builtin:shrub", originalFilename: "관목", originalUrl: "/assets/props/shrub.png", thumbnailUrl: "/assets/props/shrub.png", mimeType: "image/png", byteSize: 1, createdAt: "legacy" },
  { id: "builtin:boulder", originalFilename: "바위", originalUrl: "/assets/props/boulder.png", thumbnailUrl: "/assets/props/boulder.png", mimeType: "image/png", byteSize: 1, createdAt: "legacy" },
  { id: "builtin:fallen-log", originalFilename: "쓰러진 통나무", originalUrl: "/assets/props/fallen-log.png", thumbnailUrl: "/assets/props/fallen-log.png", mimeType: "image/png", byteSize: 1, createdAt: "legacy" },
];
const defaultImageAssetsById = new Map([...defaultImageAssets, ...legacyBuiltInImageAssets].map((asset) => [asset.id, asset]));
type Layer = "ground" | "prop" | "image";
type GroundEditTab = "terrain" | "elevation";
type PropMode = "place" | "move" | "erase";
type CellPosition = { column: number; row: number };
type MovingProp = {
  prop: PropType;
  fromColumn: number;
  fromRow: number;
  target: CellPosition | null;
};
type MovingImage = {
  index: number;
  fromColumn: number;
  fromRow: number;
  target: CellPosition | null;
};

let map = restoreDraft() ?? createInitialMap();
let waterDepths: number[] = [];
type BrushMode = "brush" | "shape";
type BrushShape = "square" | "circle" | "rectangle" | "ellipse";
type ShapeDrag = { start: CellPosition; current: CellPosition | null; erase: boolean };
let brushSize = 1;
let brushMode: BrushMode = "brush";
let brushShape: BrushShape = "square";
let shapeDrag: ShapeDrag | null = null;
let savedMapId: string | null = null;
let selectedLayer: Layer = "ground";
let selectedGroundEditTab: GroundEditTab = "terrain";
let selectedGround: GroundType = "grass";
let selectedGroundBrightness: BrightnessCorrection = 0;
let selectedTileElevation: TileElevation = 1;
let selectedProp: PropType = "broadleaf-tree";
let gridVisible = true;
let isDrawing = false;
let strokeChanged = false;
let propMode: PropMode = "place";
let movingProp: MovingProp | null = null;
let imageMode: PropMode = "place";
let imageContinuousPlacement = false;
let movingImage: MovingImage | null = null;
let imageMaterialTab: ImageMaterialTab = "default";
let selectedImageId: string | null = null;
let selectedImagePlacementIndex: number | null = null;
let selectedImageRotation = DEFAULT_IMAGE_ROTATION;
let selectedImageScale = DEFAULT_IMAGE_SCALE;
let imageTransformChanged = false;
let lastPaintedCell = "";
let history: MapDocument[] = [];
let future: MapDocument[] = [];
let saveTimer: number | undefined;
let deploymentTimeTimer: number | undefined;
let authClient: AuthClient | null = null;
let adminClient: AdminClient | null = null;
let imageLibraryClient: ImageLibraryClient | null = null;
let mapStorageClient: MapStorageClient | null = null;
let authSession: AuthSession | null = null;
let imageAssets: ImageAsset[] = [];
let imageAssetsLoaded = false;
let imageAssetsLoading = false;
const imageAssetsById = new Map<string, ImageAsset>();
const imageRenderImages = new Map<string, HTMLImageElement>();
let googleClientId = "";
let googleIdentityLoadPromise: Promise<void> | null = null;
let fileMenuOpen = false;
let saveAsMode = false;
let fullscreenFallback = false;
let pendingAvatarIcon: AvatarIcon = "initial";
let zoom = 1;
let panX = 0;
let panY = 0;
let panMode = false;
let isPanning = false;
let panStartX = 0;
let panStartY = 0;
let panOriginX = 0;
let panOriginY = 0;
let spacePressed = false;
let developerAccess = false;
let adminAccess = false;

document.querySelector<HTMLDivElement>("#app")!.innerHTML = `
  <div class="app-shell">
    <header class="topbar">
      <a class="brand" href="#workspace" aria-label="Forest Map Editor 홈">
        <span class="brand-mark" aria-hidden="true">✦</span>
        <span><strong>FOREST</strong><small>MAP EDITOR</small></span>
      </a>
      <div class="document-title">
        <label for="map-name">현재 지도</label>
        <input id="map-name" value="${escapeHtml(map.name)}" maxlength="48" />
      </div>
      <div class="top-actions">
        <div class="auth-slot" id="auth-slot"><span class="auth-note">로그인 준비 중</span></div>
        <button class="button ghost" id="undo" title="실행 취소 (Ctrl+Z)">↶ <span>실행 취소</span></button>
        <button class="button ghost" id="redo" title="다시 실행 (Ctrl+Shift+Z)">↷</button>
        <button class="icon-button fullscreen-toggle" id="toggle-fullscreen" aria-expanded="false" aria-pressed="false" title="전체 화면으로 전환" aria-label="전체 화면으로 전환">
          <svg class="fullscreen-icon" data-fullscreen-icon="expand" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 4H4v4M16 4h4v4M20 16v4h-4M4 16v4h4" /></svg>
          <svg class="fullscreen-icon hidden" data-fullscreen-icon="contract" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4v5H4M15 4v5h5M20 15h-5v5M9 20v-5H4" /></svg>
        </button>
        <div class="file-menu-wrap">
          <button class="icon-button file-menu-toggle" id="file-menu-toggle" aria-expanded="false" aria-controls="file-menu" aria-haspopup="menu" title="파일 메뉴" aria-label="파일 메뉴">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
          </button>
          <div class="file-menu hidden" id="file-menu" role="menu" aria-label="파일 기능">
            <span class="file-menu-heading">지도 작업</span>
            <button class="button export" id="save-map" role="menuitem">지도 저장</button>
            <button class="button export" id="save-map-as" role="menuitem">다른 이름으로 저장</button>
            <button class="button export" id="open-map-library" role="menuitem">저장한 지도 목록</button>
            <button class="button export" id="open-image-library" role="menuitem">내 이미지</button>
            <button class="button export" id="open-resize-map" role="menuitem">맵 크기 조정</button>
            <span class="file-menu-heading">파일 내보내기</span>
            <button class="button export" id="export-json" role="menuitem">JSON 저장</button>
            <button class="button primary" id="export-png" role="menuitem">PNG 내보내기</button>
          </div>
        </div>
      </div>
    </header>

    <main class="workspace" id="workspace">
      <aside class="tools-panel" aria-label="타일 도구">
        <div class="panel-heading"><span class="eyebrow">PALETTE</span><h2>지도 재료</h2></div>
        <div class="layer-tabs" role="tablist" aria-label="편집 레이어">
          <button class="layer-tab active" data-layer="ground">타일</button>
          <button class="layer-tab" data-layer="prop">사물</button>
          <button class="layer-tab" data-layer="image">이미지</button>
        </div>
        <section class="palette-section" id="ground-palette">
          <div class="ground-edit-tabs" role="tablist" aria-label="타일 편집 종류">
            <button class="ground-edit-tab active" type="button" data-ground-edit-tab="terrain" role="tab" aria-selected="true">지형</button>
            <button class="ground-edit-tab" type="button" data-ground-edit-tab="elevation" role="tab" aria-selected="false">레이어</button>
          </div>
          <section id="terrain-palette">
          <div class="section-label"><span>GROUND</span><span>4종</span></div>
          <div class="ground-list">
            ${groundOptions.map((item, index) => `
              <button class="ground-option ${index === 0 ? "selected" : ""}" data-ground="${item.id}">
                <span class="ground-swatch ${item.id}"></span>
                <span><strong>${item.label}</strong><small>${item.hint}</small></span><span class="check">✓</span>
              </button>`).join("")}
          </div>
          <div class="brightness-correction-control">
            <div class="section-label"><span>BRIGHTNESS</span></div>
            <div class="brightness-correction-list" role="radiogroup" aria-label="명도 보정 단계">
              ${[0, 1, 2, 3].map((level) => `
                <button class="brightness-correction-option ${level === 0 ? "selected" : ""}" type="button" data-ground-brightness="${level}" role="radio" aria-label="명도 보정 ${level === 0 ? "없음" : `${level}단계 어둡게`}" aria-checked="${level === 0}">
                  <span class="brightness-correction-swatch level-${level}" aria-hidden="true"></span>
                </button>`).join("")}
            </div>
          </div>
          </section>
          <section class="hidden" id="elevation-palette">
            <div class="section-label"><span>TILE HEIGHT</span><span>3 LEVELS</span></div>
            <div class="tile-elevation-list" role="radiogroup" aria-label="타일 높이">
              <button class="tile-elevation-option" type="button" data-tile-elevation="0" role="radio" aria-checked="false">
                <span class="tile-elevation-icon base" aria-hidden="true"></span>
                <span><strong>기본 높이</strong><small>현재 층으로 내리기</small></span>
              </button>
              <button class="tile-elevation-option selected" type="button" data-tile-elevation="1" role="radio" aria-checked="true">
                <span class="tile-elevation-icon raised" aria-hidden="true"></span>
                <span><strong>1층 올리기</strong><small>타일 한 칸 높이 올리기</small></span>
              </button>
              <button class="tile-elevation-option" type="button" data-tile-elevation="2" role="radio" aria-checked="false">
                <span class="tile-elevation-icon raised two" aria-hidden="true"></span>
                <span><strong>2층 올리기</strong><small>타일 두 칸 높이 올리기</small></span>
              </button>
            </div>
            <p class="tile-elevation-hint">물 타일은 올릴 수 없습니다. 높이를 선택해 타일을 칠하세요.</p>
          </section>
          <section class="brush-size-control" aria-label="Brush size">
            <div class="section-label"><span>BRUSH SIZE</span><output id="brush-size-output">1 × 1</output></div>
            <div class="brush-size-row">
              <input id="brush-size-range" class="transform-range" type="range" min="1" max="10" step="1" value="1" aria-label="Brush size from 1 to 10 cells" />
              <input id="brush-size-number" class="brush-size-number" type="number" min="1" max="10" step="1" value="1" aria-label="Brush size" />
            </div>
            <div class="brush-tool-list" role="group" aria-label="Tile tool mode">
              <button class="brush-tool active" type="button" data-brush-mode="brush" aria-pressed="true">Brush</button>
              <button class="brush-tool" type="button" data-brush-mode="shape" aria-pressed="false">Shape fill</button>
            </div>
            <div class="brush-shape-list" id="brush-shapes" role="group" aria-label="Brush shape">
              <button class="brush-shape active" type="button" data-brush-shape="square" aria-pressed="true">Square</button>
              <button class="brush-shape" type="button" data-brush-shape="circle" aria-pressed="false">Circle</button>
            </div>
            <div class="brush-shape-list hidden" id="fill-shapes" role="group" aria-label="Fill shape">
              <button class="brush-shape active" type="button" data-brush-shape="rectangle" aria-pressed="true">Rectangle</button>
              <button class="brush-shape" type="button" data-brush-shape="ellipse" aria-pressed="false">Ellipse</button>
            </div>
            <p class="brush-size-hint" id="brush-size-hint">Paint a square area from 1 × 1 up to 10 × 10 cells.</p>
          </section>
        </section>
        <section class="palette-section hidden" id="prop-palette">
          <div class="section-label"><span>EDIT MODE</span><span id="prop-mode-label">배치</span></div>
          <div class="prop-mode-list" role="group" aria-label="사물 편집 모드">
            <button class="prop-mode active" data-prop-mode="place" aria-pressed="true">배치</button>
            <button class="prop-mode" data-prop-mode="move" aria-pressed="false">이동</button>
            <button class="prop-mode" data-prop-mode="erase" aria-pressed="false">지우개</button>
          </div>
          <p class="prop-mode-hint" id="prop-mode-hint">사물을 누른 채 움직여 연속으로 배치합니다.</p>
          <div class="section-label"><span>OBJECTS</span><span>6종</span></div>
          <div class="prop-grid">
            ${propOptions.map((item, index) => `
              <button class="prop-option ${index === 0 ? "selected" : ""}" data-prop="${item.id}" title="${item.label}">
                <img src="${item.image}" alt="" /><span>${item.label}</span>
              </button>`).join("")}
          </div>
        </section>
        <section class="palette-section hidden" id="image-palette">
          <div class="section-label"><span>IMAGE MATERIALS</span><span id="image-material-count"></span></div>
          <div class="image-material-tabs" role="tablist" aria-label="이미지 재료 종류">
            <button class="image-material-tab active" type="button" data-image-material-tab="default" role="tab" aria-selected="true">기본 제공</button>
            <button class="image-material-tab" type="button" data-image-material-tab="mine" role="tab" aria-selected="false">나의 이미지</button>
          </div>
          <p class="image-palette-message" id="image-palette-message">로그인하면 저장한 이미지를 재료로 사용할 수 있습니다.</p>
          <div class="image-material-grid" id="image-material-grid"></div>
          <div class="section-label"><span>EDIT MODE</span><span id="image-mode-label">배치</span></div>
          <div class="prop-mode-list" role="group" aria-label="이미지 편집 모드">
            <button class="prop-mode active" data-image-mode="place" aria-pressed="true">배치</button>
            <button class="prop-mode" data-image-mode="move" aria-pressed="false">이동</button>
            <button class="prop-mode" data-image-mode="erase" aria-pressed="false">지우기</button>
          </div>
          <p class="prop-mode-hint" id="image-mode-hint">이미지를 고른 뒤 맵을 클릭하면 배치합니다.</p>
          <label class="image-continuous-toggle hidden" id="image-continuous-toggle">
            <span class="toggle-copy"><strong>연속 배치</strong><small>드래그하면 지나가는 셀마다 배치합니다.</small></span>
            <input id="image-continuous-placement" type="checkbox" role="switch" aria-label="연속 배치" />
            <span class="toggle-track" aria-hidden="true"><span class="toggle-knob"></span></span>
          </label>
        </section>
        <div class="tool-tip"><span>TIP</span> 사물은 배치·이동·지우개 모드로 편집합니다. 바닥 타일에서는 오른쪽 클릭으로 지울 수 있어요.</div>
      </aside>

      <section class="canvas-stage" aria-label="지도 편집 영역">
        <div class="stage-toolbar">
          <div class="status-dot"><i></i><span id="save-status">브라우저에 자동 저장됨</span></div>
          <div class="stage-controls">
            <div class="viewport-controls" aria-label="작업 영역 뷰포트">
              <button class="icon-button" id="zoom-out" title="축소" aria-label="축소">−</button>
              <button class="zoom-readout" id="zoom-reset" title="확대/축소와 위치 초기화" aria-label="확대/축소와 위치 초기화">100%</button>
              <button class="icon-button" id="zoom-in" title="확대" aria-label="확대">+</button>
              <button class="icon-button pan-toggle" id="toggle-pan" aria-pressed="false" title="작업 영역 이동 모드" aria-label="작업 영역 이동 모드">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 11V6.5a1.5 1.5 0 0 1 3 0V11m0-1V5.5a1.5 1.5 0 0 1 3 0V11m0-1V7a1.5 1.5 0 0 1 3 0v6m0-1v-1a1.5 1.5 0 0 1 3 0v3.5c0 4.1-2.6 6.5-6.5 6.5h-1.2c-2.1 0-3.5-.8-4.7-2.3L5 16.8a1.7 1.7 0 0 1 2.4-2.4L8 15" /></svg>
              </button>
            </div>
            <div class="stage-map-controls">
              <button class="icon-button active" id="toggle-grid" aria-pressed="true" title="격자 표시">#</button>
              <button class="icon-button" id="reset-map" title="예시 지도로 초기화">↺</button>
              <button class="icon-button" id="clear-map" title="빈 지도로 만들기">□</button>
            </div>
          </div>
        </div>
        <div class="canvas-scroll"><div class="canvas-frame" id="canvas-frame">
          <canvas id="map-canvas" aria-label="28 곱하기 18 타일 지도" tabindex="0"></canvas>
        </div></div>
        <div class="stage-footer"><span><b id="map-size">28 × 18</b> 셀</span><span id="cursor-status">셀 위에 커서를 올려보세요</span><span class="footer-links"><span id="developer-access" class="developer-access">Developer: checking</span> · <span id="d1-usage" class="d1-usage">--</span> · <span id="deployment-time" class="deployment-time">Deployment: checking</span> · 로컬 초안 · <button type="button" id="open-page-qr">page qr</button> · <a href="https://mapedit.pages.dev/cdn-cgi/trace" target="_blank" rel="noopener noreferrer">cdn trace</a> · <a href="https://github.com/octopus7/mapeditor" target="_blank" rel="noopener noreferrer">github</a></span></div>
      </section>

      <aside class="reference-panel" aria-label="이미지 상세 제어">
        <div class="panel-heading"><span class="eyebrow">IMAGE DETAILS</span><h2 id="image-detail-title">이미지 상세 제어</h2><p id="image-detail-description">맵에 이미지를 배치하면 이 영역에서 정밀하게 조정할 수 있습니다.</p></div>
        <div class="image-detail-empty" id="image-detail-empty">이미지를 배치하거나 이미지 이동 모드에서 대상을 선택해 주세요.</div>
        <section class="image-detail-content hidden" id="image-detail-content" aria-label="선택 이미지 변환">
          <div class="image-detail-preview"><img id="selected-image-preview" alt="" /><span id="selected-image-name"></span></div>
          <div class="image-transform-controls" aria-label="이미지 변환 설정">
            <div class="section-label"><span>TRANSFORM</span><span id="image-transform-summary">0° · 200%</span></div>
            <div class="transform-control">
              <div class="transform-control-header"><label for="image-rotation">회전</label><output id="image-rotation-value">0°</output></div>
              <div class="transform-number-row"><span class="number-input-wrap"><input id="image-rotation" type="number" min="0" max="345" step="15" value="0" /><b>°</b></span><button class="icon-button transform-reset-button" type="button" id="reset-image-rotation" title="회전 기본값 복원" aria-label="회전 기본값 복원"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 8V4m0 0h4M5 4a8 8 0 1 1-1.2 11.2" /></svg></button></div>
              <input class="transform-range" id="image-rotation-slider" type="range" min="0" max="345" step="15" value="0" aria-label="회전 슬라이더" />
            </div>
            <div class="transform-control">
              <div class="transform-control-header"><label for="image-scale">스케일</label><output id="image-scale-value">200%</output></div>
              <div class="transform-number-row"><span class="number-input-wrap"><input id="image-scale" type="number" min="25" max="600" step="25" value="200" /><b>%</b></span><button class="icon-button transform-reset-button" type="button" id="reset-image-scale" title="스케일 기본값 복원" aria-label="스케일 기본값 복원"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7V4h3M4 4l4 4m9 9h3v3m0 0-4-4M8 8h8v8H8z" /></svg></button></div>
              <input class="transform-range" id="image-scale-slider" type="range" min="25" max="600" step="25" value="200" aria-label="스케일 슬라이더" />
            </div>
            <button class="button ghost image-transform-reset" type="button" id="reset-image-transform">기본값 복원</button>
            <p class="image-detail-hint" id="image-detail-hint">회전 0° · 스케일 200%</p>
          </div>
        </section>
      </aside>
    </main>
  </div>
  <dialog id="page-qr-dialog" class="qr-dialog" aria-labelledby="page-qr-title">
    <strong id="page-qr-title">페이지 접속 QR 코드</strong>
    <img src="/assets/mapedit-page-qr.svg" alt="https://mapedit.pages.dev 접속 QR 코드" />
    <span>아무 곳이나 누르면 닫힙니다.</span>
  </dialog>
  <dialog id="profile-dialog" class="profile-dialog">
    <form method="dialog">
      <div class="profile-heading"><span class="profile-avatar" id="profile-avatar">?</span><div><small>Google 계정</small><strong id="profile-email"></strong></div></div>
      <label for="profile-name">표시 이름</label>
      <input id="profile-name" maxlength="40" autocomplete="nickname" />
      <fieldset class="avatar-settings">
        <legend>프로필 아이콘</legend>
        <div class="avatar-options" aria-label="프로필 아이콘 선택">
          ${avatarOptions.map((option) => `<button type="button" data-avatar-icon="${option.id}" aria-pressed="false" title="${option.label}"><span aria-hidden="true">${option.glyph}</span><small>${option.label}</small></button>`).join("")}
        </div>
      </fieldset>
      <p class="profile-message" id="profile-message">변경한 이름과 아이콘은 계정 설정에 저장됩니다.</p>
      <div class="profile-actions"><button class="button ghost" value="cancel">닫기</button><button class="button danger" type="button" id="logout">로그아웃</button><button class="button primary" type="button" id="save-profile">설정 저장</button></div>
    </form>
  </dialog>
  <dialog id="auth-debug-dialog" class="auth-debug-dialog" aria-labelledby="auth-debug-title">
    <form method="dialog">
      <h2 id="auth-debug-title">개발자 로그인 진단</h2>
      <p id="auth-debug-message">허용된 개발자 접속에서만 표시되는 상세 오류입니다.</p>
      <pre id="auth-debug-details"></pre>
      <div class="dialog-actions"><button class="button primary">닫기</button></div>
    </form>
  </dialog>
  <dialog id="image-debug-dialog" class="auth-debug-dialog" aria-labelledby="image-debug-title">
    <form method="dialog">
      <h2 id="image-debug-title">이미지 저장 개발자 진단</h2>
      <p id="image-debug-message">허용된 개발자 접속에서만 표시되는 상세 오류입니다.</p>
      <pre id="image-debug-details"></pre>
      <div class="dialog-actions"><button class="button primary">닫기</button></div>
    </form>
  </dialog>
  <dialog id="map-debug-dialog" class="auth-debug-dialog" aria-labelledby="map-debug-title">
    <form method="dialog">
      <h2 id="map-debug-title">Map save developer diagnostics</h2>
      <p id="map-debug-message">Detailed errors are shown only for allowlisted developer access.</p>
      <pre id="map-debug-details"></pre>
      <div class="dialog-actions"><button class="button primary">Close</button></div>
    </form>
  </dialog>
  <dialog id="map-save-dialog" class="editor-dialog">
    <form method="dialog">
      <h2 id="map-save-title">지도 저장</h2>
      <p class="dialog-note">로그인한 사용자만 개인 지도 목록에 저장할 수 있습니다.</p>
      <label for="map-save-name">지도 이름</label>
      <input id="map-save-name" maxlength="80" autocomplete="off" />
      <p class="dialog-message" id="map-save-message"></p>
      <div class="dialog-actions"><button class="button ghost" value="cancel">취소</button><button class="button primary" type="button" id="confirm-map-save">저장</button></div>
    </form>
  </dialog>
  <dialog id="map-library-dialog" class="editor-dialog map-library-dialog">
    <form method="dialog">
      <h2>저장한 지도 목록</h2>
      <p class="dialog-note" id="map-library-message">로그인 후 저장한 지도를 불러올 수 있습니다.</p>
      <div id="map-library-list" class="map-library-list"></div>
      <div class="dialog-actions"><button class="button ghost" value="cancel">닫기</button></div>
    </form>
  </dialog>
  <dialog id="resize-map-dialog" class="editor-dialog resize-map-dialog">
    <form method="dialog">
      <h2>맵 크기 조정</h2>
      <p class="dialog-note">포토샵 캔버스처럼 새 영역을 지정합니다. 기본 기준은 가운데이며, 적용 전 초록색은 추가 영역, 붉은색은 잘리는 영역입니다.</p>
      <div class="resize-fields"><label for="resize-columns">가로<input id="resize-columns" type="number" min="8" max="200" /></label><label for="resize-rows">세로<input id="resize-rows" type="number" min="8" max="200" /></label></div>
      <fieldset class="resize-anchor-fieldset"><legend>기존 맵을 붙일 기준 위치</legend><input id="resize-anchor" type="hidden" value="center" />
        <div class="resize-anchor-grid" role="group" aria-label="기존 맵 기준 위치">
          <button type="button" class="resize-anchor-option" data-resize-anchor="top-left" aria-label="왼쪽 위">↖<small>왼쪽 위</small></button>
          <button type="button" class="resize-anchor-option" data-resize-anchor="top" aria-label="위쪽 가운데">↑<small>위쪽 가운데</small></button>
          <button type="button" class="resize-anchor-option" data-resize-anchor="top-right" aria-label="오른쪽 위">↗<small>오른쪽 위</small></button>
          <button type="button" class="resize-anchor-option" data-resize-anchor="left" aria-label="왼쪽 가운데">←<small>왼쪽 가운데</small></button>
          <button type="button" class="resize-anchor-option active" data-resize-anchor="center" aria-label="가운데" aria-pressed="true">＋<small>가운데</small></button>
          <button type="button" class="resize-anchor-option" data-resize-anchor="right" aria-label="오른쪽 가운데">→<small>오른쪽 가운데</small></button>
          <button type="button" class="resize-anchor-option" data-resize-anchor="bottom-left" aria-label="왼쪽 아래">↙<small>왼쪽 아래</small></button>
          <button type="button" class="resize-anchor-option" data-resize-anchor="bottom" aria-label="아래쪽 가운데">↓<small>아래쪽 가운데</small></button>
          <button type="button" class="resize-anchor-option" data-resize-anchor="bottom-right" aria-label="오른쪽 아래">↘<small>오른쪽 아래</small></button>
        </div>
      </fieldset>
      <div class="resize-preview-wrap"><canvas id="resize-preview" class="resize-preview" aria-label="맵 크기 조정 미리보기"></canvas><div class="resize-preview-legend"><span class="resize-preview-grow">늘어나는 영역</span><span class="resize-preview-crop">잘리는 영역</span></div></div>
      <p class="dialog-message" id="resize-map-message"></p>
      <div class="dialog-actions"><button class="button ghost" value="cancel">취소</button><button class="button primary" type="button" id="confirm-resize-map">적용</button></div>
    </form>
  </dialog>
`;

function setupAuthUi(): void {
  const authSlot = document.querySelector<HTMLDivElement>("#auth-slot");
  const fileMenu = document.querySelector<HTMLElement>("#file-menu");
  if (!authSlot || !fileMenu) return;

  const loginTrigger = document.createElement("button");
  loginTrigger.type = "button";
  loginTrigger.className = "button ghost login-trigger";
  loginTrigger.id = "login-trigger";
  loginTrigger.textContent = "Google 로그인";

  const accountSection = document.createElement("div");
  accountSection.className = "account-menu-section hidden";
  accountSection.id = "account-menu-section";
  accountSection.innerHTML = `
    <span class="file-menu-heading">계정</span>
    <div id="account-menu-slot" class="account-menu-slot"></div>`;
  accountSection.append(loginTrigger);
  fileMenu.prepend(accountSection);

  const adminSection = document.createElement("div");
  adminSection.className = "admin-menu-section hidden";
  adminSection.id = "admin-menu-section";
  adminSection.innerHTML = `
    <span class="file-menu-heading">개발자 메뉴</span>
    <button class="button export" id="open-admin-users" role="menuitem">사용자 관리</button>`;
  fileMenu.prepend(adminSection);

  const authDialog = document.createElement("dialog");
  authDialog.id = "auth-dialog";
  authDialog.className = "auth-dialog";
  authDialog.setAttribute("aria-labelledby", "auth-dialog-title");
  authDialog.innerHTML = `
    <form method="dialog">
      <div class="auth-dialog-heading">
        <span class="eyebrow">ACCOUNT</span>
        <h2 id="auth-dialog-title">Google 로그인</h2>
        <p>Google 계정으로 로그인하면 나만의 지도와 이미지를 저장할 수 있습니다.</p>
      </div>
      <div class="auth-dialog-slot"></div>
      <div class="dialog-actions"><button class="button ghost" value="cancel">닫기</button></div>
    </form>`;
  authDialog.querySelector<HTMLDivElement>(".auth-dialog-slot")!.append(authSlot);
  document.body.append(authDialog);

  const adminDialog = document.createElement("dialog");
  adminDialog.id = "admin-dialog";
  adminDialog.className = "editor-dialog admin-dialog";
  adminDialog.innerHTML = `
    <form method="dialog">
      <h2>사용자 관리</h2>
      <p class="dialog-note">개발자 또는 관리자 권한으로 로그인한 사용자의 저장된 맵을 열람합니다.</p>
      <p class="dialog-message" id="admin-message"></p>
      <div class="admin-browser">
        <section class="admin-users-pane" aria-label="사용자 목록">
          <strong class="admin-pane-title">사용자 목록</strong>
          <div id="admin-user-list" class="admin-user-list"></div>
        </section>
        <section class="admin-maps-pane" aria-label="선택한 사용자의 맵 목록">
          <strong class="admin-pane-title" id="admin-maps-title">저장된 맵</strong>
          <div id="admin-map-list" class="admin-map-list"><span class="empty-library">사용자를 선택하세요.</span></div>
        </section>
      </div>
      <div class="dialog-actions"><button class="button ghost" value="cancel">닫기</button></div>
    </form>`;
  document.body.append(adminDialog);
}
setupAuthUi();

function setupDeveloperAccessUi(): void {
  const existing = document.querySelector<HTMLSpanElement>("#developer-access");
  if (!existing) return;
  const button = document.createElement("button");
  button.type = "button";
  button.id = existing.id;
  button.className = existing.className;
  button.textContent = existing.textContent;
  button.disabled = true;
  existing.replaceWith(button);
}
setupDeveloperAccessUi();

const canvas = document.querySelector<HTMLCanvasElement>("#map-canvas")!;
const canvasFrame = document.querySelector<HTMLDivElement>("#canvas-frame")!;
const canvasScroll = document.querySelector<HTMLDivElement>(".canvas-scroll")!;
const context = canvas.getContext("2d")!;
const MAX_GROUND_TEXTURE_CACHE_ENTRIES = 1024;
const groundTextureCache = new Map<string, HTMLCanvasElement>();
const brightnessCorrectionCanvas = document.createElement("canvas");
const brightnessCorrectionContext = brightnessCorrectionCanvas.getContext("2d")!;
const transitionCorrectionCanvas = document.createElement("canvas");
const transitionCorrectionContext = transitionCorrectionCanvas.getContext("2d")!;
const transitionCorrectionPatchCanvas = document.createElement("canvas");
const transitionCorrectionPatchContext = transitionCorrectionPatchCanvas.getContext("2d")!;
const transitionCorrectionMaskCanvas = document.createElement("canvas");
const transitionCorrectionMaskContext = transitionCorrectionMaskCanvas.getContext("2d")!;
const transitionCorrectionBlurCanvas = document.createElement("canvas");
const transitionCorrectionBlurContext = transitionCorrectionBlurCanvas.getContext("2d")!;
const TRANSITION_CORRECTION_BLUR = CELL_SIZE / 8;
let transitionCorrectionCacheReady = false;
let transitionCorrectionBuildIndex = 0;
let transitionCorrectionBuildScheduled = false;
const brightnessCorrectionPatchCanvas = document.createElement("canvas");
const brightnessCorrectionPatchContext = brightnessCorrectionPatchCanvas.getContext("2d")!;
brightnessCorrectionPatchContext.imageSmoothingEnabled = false;
const brightnessCorrectionDirtyCells = new Set<number>();
const brightnessCorrectionColorCache = new Map<string, string>();
let brightnessCorrectionCacheReady = false;
let brightnessCorrectionActiveCellCount = 0;
let brightnessCorrectionFullBuild: { nextRow: number; activeCellCount: number } | null = null;
let brightnessCorrectionBuildScheduled = false;
function syncCanvasSize(): void {
  canvas.width = map.columns * CELL_SIZE;
  canvas.height = map.rows * CELL_SIZE;
  brightnessCorrectionCanvas.width = canvas.width;
  brightnessCorrectionCanvas.height = canvas.height;
  transitionCorrectionCanvas.width = canvas.width;
  transitionCorrectionCanvas.height = canvas.height;
  invalidateTransitionCorrectionCache();
  invalidateBrightnessCorrectionCache();
  document.querySelector("#map-size")!.textContent = `${map.columns} × ${map.rows}`;
  canvas.setAttribute("aria-label", `${map.columns} 곱하기 ${map.rows} 타일 지도`);
}
syncCanvasSize();
const propImages = new Map<PropType, HTMLImageElement>();
for (const prop of propOptions) {
  const image = new Image();
  image.src = prop.image;
  image.addEventListener("load", render);
  propImages.set(prop.id, image);
}

function restoreDraft(): MapDocument | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? deserializeMap(raw) : null;
}
function escapeHtml(value: string): string {
  const characters: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#039;", '"': "&quot;" };
  return value.replace(/[&<>'"]/g, (character) => characters[character]);
}
const GROUND_NOISE_TEXTURE_SIZE = 256;
const GROUND_NOISE_GRID_SIZE = 32;
const GROUND_NOISE_PATTERN_SEED = 0x6d617065;
function createGroundNoisePattern(): Int8Array {
  const coarsePattern = new Int8Array(GROUND_NOISE_GRID_SIZE * GROUND_NOISE_GRID_SIZE);
  let state = GROUND_NOISE_PATTERN_SEED;
  for (let index = 0; index < coarsePattern.length; index += 1) {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    coarsePattern[index] = (state % 11) - 5;
  }
  return coarsePattern;
}
const GROUND_NOISE_PATTERN = createGroundNoisePattern();
const GROUND_NOISE_TEXTURE = new Int8Array(GROUND_NOISE_TEXTURE_SIZE * GROUND_NOISE_TEXTURE_SIZE);
let groundNoiseReady = false;
let groundNoiseBuildRow = 0;
let groundNoiseBuildScheduled = false;
function processGroundNoiseBuild(): void {
  groundNoiseBuildScheduled = false;
  const startedAt = performance.now();
  const sampleStep = GROUND_NOISE_TEXTURE_SIZE / GROUND_NOISE_GRID_SIZE;
  const smoothStep = (value: number): number => value * value * (3 - 2 * value);
  while (groundNoiseBuildRow < GROUND_NOISE_TEXTURE_SIZE && performance.now() - startedAt < 6) {
    const y = groundNoiseBuildRow;
    const gridY = y / sampleStep;
    const gridY0 = Math.floor(gridY) % GROUND_NOISE_GRID_SIZE;
    const gridY1 = (gridY0 + 1) % GROUND_NOISE_GRID_SIZE;
    const yWeight = smoothStep(gridY - Math.floor(gridY));
    for (let x = 0; x < GROUND_NOISE_TEXTURE_SIZE; x += 1) {
      const gridX = x / sampleStep;
      const gridX0 = Math.floor(gridX) % GROUND_NOISE_GRID_SIZE;
      const gridX1 = (gridX0 + 1) % GROUND_NOISE_GRID_SIZE;
      const xWeight = smoothStep(gridX - Math.floor(gridX));
      const top = GROUND_NOISE_PATTERN[gridY0 * GROUND_NOISE_GRID_SIZE + gridX0] * (1 - xWeight)
        + GROUND_NOISE_PATTERN[gridY0 * GROUND_NOISE_GRID_SIZE + gridX1] * xWeight;
      const bottom = GROUND_NOISE_PATTERN[gridY1 * GROUND_NOISE_GRID_SIZE + gridX0] * (1 - xWeight)
        + GROUND_NOISE_PATTERN[gridY1 * GROUND_NOISE_GRID_SIZE + gridX1] * xWeight;
      GROUND_NOISE_TEXTURE[y * GROUND_NOISE_TEXTURE_SIZE + x] = Math.round(top * (1 - yWeight) + bottom * yWeight);
    }
    groundNoiseBuildRow += 1;
  }
  if (groundNoiseBuildRow < GROUND_NOISE_TEXTURE_SIZE) {
    groundNoiseBuildScheduled = true;
    window.setTimeout(processGroundNoiseBuild, 0);
    return;
  }
  groundNoiseReady = true;
  scheduleRender();
}
function scheduleGroundNoiseBuild(): void {
  if (groundNoiseReady || groundNoiseBuildScheduled) return;
  groundNoiseBuildScheduled = true;
  window.setTimeout(processGroundNoiseBuild, 0);
}
function textureNoise(x: number, y: number): number {
  if (!groundNoiseReady) return 0;
  const textureX = ((x % GROUND_NOISE_TEXTURE_SIZE) + GROUND_NOISE_TEXTURE_SIZE) % GROUND_NOISE_TEXTURE_SIZE;
  const textureY = ((y % GROUND_NOISE_TEXTURE_SIZE) + GROUND_NOISE_TEXTURE_SIZE) % GROUND_NOISE_TEXTURE_SIZE;
  return GROUND_NOISE_TEXTURE[textureY * GROUND_NOISE_TEXTURE_SIZE + textureX];
}
function colorNoise(column: number, row: number): number {
  return textureNoise(column * CELL_SIZE, row * CELL_SIZE);
}
const groundPalettes: Record<GroundType, [number, number, number]> = {
  grass: [104, 151, 85], dirt: [162, 126, 79], stone: [127, 132, 121], water: [58, 137, 153],
};
function mixGroundColor(palette: [number, number, number], noise: number): [number, number, number] {
  const amount = Math.min(Math.abs(noise), 5) / 5 * .03;
  const target = noise >= 0 ? 255 : 0;
  return palette.map((channel) => Math.round(channel * (1 - amount) + target * amount)) as [number, number, number];
}
const GROUND_TEXTURE_ATLAS_SIZE = GROUND_NOISE_TEXTURE_SIZE + CELL_SIZE;
const groundTextureAtlases = new Map<string, HTMLCanvasElement>();
const groundTextureBuildQueue: Array<{
  key: string;
  ground: GroundType;
  depth: number;
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  image: ImageData;
  nextRow: number;
}> = [];
const groundTextureBuildPending = new Set<string>();
let groundTextureBuildScheduled = false;
function getGroundBaseColor(ground: GroundType, depth: number): [number, number, number] {
  let [r, g, b] = groundPalettes[ground];
  if (ground !== "water") return [r, g, b];
  const depthRatio = depth / 8;
  const deepWater: [number, number, number] = [22, 83, 101];
  r = Math.round(r * (1 - depthRatio * .72) + deepWater[0] * (depthRatio * .72));
  g = Math.round(g * (1 - depthRatio * .72) + deepWater[1] * (depthRatio * .72));
  b = Math.round(b * (1 - depthRatio * .72) + deepWater[2] * (depthRatio * .72));
  return [r, g, b];
}
function getGroundTextureDepth(column: number, row: number, ground: GroundType): number {
  return ground === "water" ? Math.min(Math.max(waterDepths[cellIndex(map, column, row)] ?? 0, 0), 8) : 0;
}
function enqueueGroundTextureAtlas(ground: GroundType, depth: number): void {
  if (!groundNoiseReady) {
    scheduleGroundNoiseBuild();
    return;
  }
  const key = `${ground},${depth}`;
  if (groundTextureAtlases.has(key) || groundTextureBuildPending.has(key)) return;
  const canvas = document.createElement("canvas");
  canvas.width = GROUND_TEXTURE_ATLAS_SIZE;
  canvas.height = GROUND_TEXTURE_ATLAS_SIZE;
  const context = canvas.getContext("2d")!;
  context.imageSmoothingEnabled = false;
  groundTextureBuildQueue.push({
    key,
    ground,
    depth,
    canvas,
    context,
    image: context.createImageData(GROUND_TEXTURE_ATLAS_SIZE, GROUND_TEXTURE_ATLAS_SIZE),
    nextRow: 0,
  });
  groundTextureBuildPending.add(key);
  if (!groundTextureBuildScheduled) {
    groundTextureBuildScheduled = true;
    window.setTimeout(processGroundTextureBuildQueue, 0);
  }
}
function processGroundTextureBuildQueue(): void {
  groundTextureBuildScheduled = false;
  const startedAt = performance.now();
  while (groundTextureBuildQueue.length > 0 && performance.now() - startedAt < 6) {
    const build = groundTextureBuildQueue[0];
    const [r, g, b] = getGroundBaseColor(build.ground, build.depth);
    const baseColor: [number, number, number] = [r, g, b];
    const pixels = build.image.data;
    while (build.nextRow < GROUND_TEXTURE_ATLAS_SIZE && performance.now() - startedAt < 6) {
      const localY = build.nextRow;
      const noiseY = localY % GROUND_NOISE_TEXTURE_SIZE;
      for (let localX = 0; localX < GROUND_TEXTURE_ATLAS_SIZE; localX += 1) {
        const noiseX = localX % GROUND_NOISE_TEXTURE_SIZE;
        const [pixelR, pixelG, pixelB] = mixGroundColor(baseColor, textureNoise(noiseX, noiseY));
        const pixelIndex = (localY * GROUND_TEXTURE_ATLAS_SIZE + localX) * 4;
        pixels[pixelIndex] = pixelR;
        pixels[pixelIndex + 1] = pixelG;
        pixels[pixelIndex + 2] = pixelB;
        pixels[pixelIndex + 3] = 255;
      }
      build.nextRow += 1;
    }
    if (build.nextRow < GROUND_TEXTURE_ATLAS_SIZE) break;
    build.context.putImageData(build.image, 0, 0);
    groundTextureAtlases.set(build.key, build.canvas);
    groundTextureBuildPending.delete(build.key);
    groundTextureBuildQueue.shift();
  }
  if (groundTextureBuildQueue.length > 0) {
    groundTextureBuildScheduled = true;
    window.setTimeout(processGroundTextureBuildQueue, 0);
  } else {
    invalidateTransitionCorrectionCache();
    scheduleRender();
  }
}
function getGroundTextureAtlas(column: number, row: number, ground: GroundType): HTMLCanvasElement | null {
  const depth = getGroundTextureDepth(column, row, ground);
  const key = `${ground},${depth}`;
  const cached = groundTextureAtlases.get(key);
  if (cached) return cached;
  enqueueGroundTextureAtlas(ground, depth);
  return null;
}
function drawGroundTextureTo(
  targetContext: CanvasRenderingContext2D,
  column: number,
  row: number,
  ground: GroundType,
  x = column * CELL_SIZE,
  y = row * CELL_SIZE,
): void {
  const depth = getGroundTextureDepth(column, row, ground);
  const atlas = getGroundTextureAtlas(column, row, ground);
  if (atlas) {
    const sourceX = ((column * CELL_SIZE) % GROUND_NOISE_TEXTURE_SIZE + GROUND_NOISE_TEXTURE_SIZE) % GROUND_NOISE_TEXTURE_SIZE;
    const sourceY = ((row * CELL_SIZE) % GROUND_NOISE_TEXTURE_SIZE + GROUND_NOISE_TEXTURE_SIZE) % GROUND_NOISE_TEXTURE_SIZE;
    targetContext.drawImage(atlas, sourceX, sourceY, CELL_SIZE, CELL_SIZE, x, y, CELL_SIZE, CELL_SIZE);
  } else {
    const [r, g, b] = getGroundBaseColor(ground, depth);
    targetContext.fillStyle = `rgb(${r}, ${g}, ${b})`;
    targetContext.fillRect(x, y, CELL_SIZE, CELL_SIZE);
  }
  targetContext.save(); targetContext.globalAlpha = 0.22;
  if (ground === "grass") {
    targetContext.strokeStyle = "#d9e6a2"; targetContext.beginPath();
    targetContext.moveTo(x + 9, y + 25); targetContext.lineTo(x + 11, y + 20); targetContext.moveTo(x + 25, y + 15); targetContext.lineTo(x + 27, y + 10); targetContext.stroke();
  } else if (ground === "water") {
    targetContext.strokeStyle = "#d5f2e9"; targetContext.beginPath();
    targetContext.moveTo(x + 5, y + 12); targetContext.quadraticCurveTo(x + 13, y + 9, x + 21, y + 12);
    targetContext.moveTo(x + 15, y + 25); targetContext.quadraticCurveTo(x + 23, y + 22, x + 31, y + 25); targetContext.stroke();
  } else {
    targetContext.fillStyle = ground === "stone" ? "#e2e0cf" : "#6e4c2f";
    targetContext.beginPath(); targetContext.arc(x + 10, y + 11, ground === "stone" ? 2.5 : 1.4, 0, Math.PI * 2); targetContext.fill();
    targetContext.beginPath(); targetContext.arc(x + 26, y + 25, ground === "stone" ? 3 : 1.2, 0, Math.PI * 2); targetContext.fill();
  }
  targetContext.restore();
}
function drawGroundTexture(column: number, row: number, ground: GroundType): void {
  drawGroundTextureTo(context, column, row, ground);
}
type WaterBankDirection = "N" | "E" | "S" | "W";
const waterBankPalettes: Record<GroundType, [number, number, number]> = {
  grass: [91, 62, 36],
  dirt: [111, 72, 40],
  stone: [78, 78, 69],
  water: [35, 84, 91],
};
function drawWaterBankFace(column: number, row: number, ground: GroundType, direction: WaterBankDirection): void {
  const targetColumn = direction === "E" ? column + 1 : direction === "W" ? column - 1 : column;
  const targetRow = direction === "S" ? row + 1 : direction === "N" ? row - 1 : row;
  if (targetColumn < 0 || targetColumn >= map.columns || targetRow < 0 || targetRow >= map.rows) return;
  const targetDirection: WaterBankDirection = direction === "N"
    ? "S"
    : direction === "E"
      ? "W"
      : direction === "S"
        ? "N"
        : "E";
  const x = targetColumn * CELL_SIZE;
  const y = targetRow * CELL_SIZE;
  // Keep the correction on the adjacent water tile. From the land tile,
  // 6 o'clock gets the deepest drop while 12 o'clock matches the side width.
  const thickness = direction === "S" ? CELL_SIZE * .40 : CELL_SIZE * .07;
  const [r, g, b] = waterBankPalettes[ground];
  const soil = `rgb(${r}, ${g}, ${b})`;
  const dark = `rgb(${Math.round(r * .58)}, ${Math.round(g * .58)}, ${Math.round(b * .58)})`;
  let faceX = x;
  let faceY = y;
  let faceWidth = CELL_SIZE;
  let faceHeight = thickness;
  let gradient: CanvasGradient;
  if (targetDirection === "N") {
    gradient = context.createLinearGradient(0, y + thickness, 0, y);
  } else if (targetDirection === "E") {
    faceX = x + CELL_SIZE - thickness;
    faceWidth = thickness;
    faceHeight = CELL_SIZE;
    gradient = context.createLinearGradient(x + CELL_SIZE - thickness, 0, x + CELL_SIZE, 0);
  } else if (targetDirection === "S") {
    faceY = y + CELL_SIZE - thickness;
    faceHeight = thickness;
    gradient = context.createLinearGradient(0, y + CELL_SIZE - thickness, 0, y + CELL_SIZE);
  } else {
    faceWidth = thickness;
    faceHeight = CELL_SIZE;
    gradient = context.createLinearGradient(x, 0, x + thickness, 0);
  }
  gradient.addColorStop(0, soil);
  gradient.addColorStop(.78, soil);
  gradient.addColorStop(1, dark);
  context.save();
  context.fillStyle = gradient;
  context.fillRect(faceX, faceY, faceWidth, faceHeight);
  context.globalAlpha = .56;
  context.strokeStyle = dark;
  context.lineWidth = 1;
  context.beginPath();
  if (targetDirection === "N" || targetDirection === "S") {
    const lineY = targetDirection === "N" ? y + thickness : y + CELL_SIZE - thickness;
    context.moveTo(x, lineY); context.lineTo(x + CELL_SIZE, lineY);
  } else {
    const lineX = targetDirection === "W" ? x + thickness : x + CELL_SIZE - thickness;
    context.moveTo(lineX, y); context.lineTo(lineX, y + CELL_SIZE);
  }
  context.stroke();
  context.restore();
}
function drawWaterBank(column: number, row: number, ground: GroundType, mask: number): void {
  const faces: Array<[number, WaterBankDirection]> = [
    [NEIGHBOR_MASK.N, "N"], [NEIGHBOR_MASK.E, "E"], [NEIGHBOR_MASK.S, "S"], [NEIGHBOR_MASK.W, "W"],
  ];
  for (const [bit, direction] of faces) {
    if (mask & bit) drawWaterBankFace(column, row, ground, direction);
  }
}
type WaterBankCornerDirection = "NE" | "SE" | "SW" | "NW";
function drawWaterBankCornerFace(column: number, row: number, ground: GroundType, direction: WaterBankCornerDirection): void {
  const targetColumn = direction === "NE" || direction === "SE" ? column + 1 : column - 1;
  const targetRow = direction === "NE" || direction === "NW" ? row - 1 : row + 1;
  if (targetColumn < 0 || targetColumn >= map.columns || targetRow < 0 || targetRow >= map.rows) return;
  const targetCorner: WaterBankCornerDirection = direction === "NE"
    ? "SW"
    : direction === "SE"
      ? "NW"
      : direction === "SW"
        ? "NE"
        : "SE";
  const x = targetColumn * CELL_SIZE;
  const y = targetRow * CELL_SIZE;
  const edge = CELL_SIZE / 8;
  const [r, g, b] = waterBankPalettes[ground];
  const soil = `rgb(${r}, ${g}, ${b})`;
  const dark = `rgb(${Math.round(r * .58)}, ${Math.round(g * .58)}, ${Math.round(b * .58)})`;
  context.save();
  context.beginPath();
  context.rect(x, y, CELL_SIZE, CELL_SIZE);
  context.clip();
  const centerX = targetCorner.includes("E") ? x + CELL_SIZE : x;
  const centerY = targetCorner.includes("S") ? y + CELL_SIZE : y;
  const gradient = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, edge * 1.8);
  gradient.addColorStop(0, dark);
  gradient.addColorStop(.52, soil);
  gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
  context.globalAlpha = .78;
  context.fillStyle = gradient;
  context.beginPath();
  context.arc(centerX, centerY, edge * 1.8, 0, Math.PI * 2);
  context.fill();
  context.restore();
}
function drawWaterBankCorners(column: number, row: number, ground: GroundType, mask: number): void {
  const corners: Array<[number, WaterBankCornerDirection]> = [
    [NEIGHBOR_MASK.NE, "NE"], [NEIGHBOR_MASK.SE, "SE"], [NEIGHBOR_MASK.SW, "SW"], [NEIGHBOR_MASK.NW, "NW"],
  ];
  for (const [bit, direction] of corners) if (mask & bit) drawWaterBankCornerFace(column, row, ground, direction);
}
const raisedTileSidePalettes: Record<GroundType, [number, number, number]> = {
  grass: [74, 50, 30], dirt: [91, 58, 34], stone: [73, 74, 66], water: [35, 84, 91],
};
const RAISED_SIDE_DEPTH = CELL_SIZE / 3;
function drawRaisedTileFace(column: number, row: number, ground: GroundType, elevation: TileElevation, direction: "E" | "S" | "W"): void {
  const x = column * CELL_SIZE;
  const y = row * CELL_SIZE;
  const [r, g, b] = raisedTileSidePalettes[ground];
  const faceDepth = direction === "S" ? CELL_SIZE * elevation : RAISED_SIDE_DEPTH;
  const shadow = `rgb(${Math.round(r * .72)}, ${Math.round(g * .72)}, ${Math.round(b * .72)})`;
  const mid = `rgb(${Math.round(r * .58)}, ${Math.round(g * .58)}, ${Math.round(b * .58)})`;
  const dark = `rgb(${Math.round(r * .46)}, ${Math.round(g * .46)}, ${Math.round(b * .46)})`;
  const highlight = `rgb(${Math.min(255, Math.round(r * 1.18))}, ${Math.min(255, Math.round(g * 1.18))}, ${Math.min(255, Math.round(b * 1.18))})`;
  const faceX = direction === "W" ? x - faceDepth : direction === "E" ? x + CELL_SIZE : x;
  const faceY = direction === "S" ? y + CELL_SIZE : y;
  const gradient = direction === "S"
    ? context.createLinearGradient(0, faceY, 0, faceY + faceDepth)
    : context.createLinearGradient(faceX, 0, faceX + (direction === "W" ? faceDepth : faceDepth), 0);
  gradient.addColorStop(0, shadow);
  gradient.addColorStop(.35, mid);
  gradient.addColorStop(.72, shadow);
  gradient.addColorStop(1, dark);
  context.save();
  context.fillStyle = gradient;
  const jaggedBottom: Array<[number, number]> = [];
  if (direction === "S") {
    const boundarySegments = 8;
    for (let segment = 0; segment <= boundarySegments; segment += 1) {
      const segmentX = faceX + (CELL_SIZE * segment) / boundarySegments;
      const noise = colorNoise(column * 5 + segment, row * 7 + elevation * 3);
      const uneven = Math.max(-3.5, Math.min(4.5, noise * .72 + (segment % 3 === 0 ? .7 : -.25)));
      jaggedBottom.push([segmentX, faceY + faceDepth - 1 + uneven]);
    }
    context.beginPath();
    context.moveTo(faceX, faceY);
    context.lineTo(faceX + CELL_SIZE, faceY);
    for (let point = jaggedBottom.length - 1; point >= 0; point -= 1) {
      context.lineTo(jaggedBottom[point][0], jaggedBottom[point][1]);
    }
    context.closePath();
    context.fill();
  } else {
    context.fillRect(faceX, faceY, faceDepth, CELL_SIZE);
  }
  context.globalAlpha = .58;
  context.strokeStyle = dark;
  context.lineWidth = 1;
  if (direction === "S") {
    context.beginPath();
    context.moveTo(jaggedBottom[0][0], jaggedBottom[0][1]);
    for (let point = 1; point < jaggedBottom.length; point += 1) {
      context.lineTo(jaggedBottom[point][0], jaggedBottom[point][1]);
    }
    context.stroke();
    context.strokeStyle = highlight;
    context.globalAlpha = .35;
    context.beginPath();
    context.moveTo(x, faceY + 1); context.lineTo(x + CELL_SIZE, faceY + 1);
    context.stroke();
    context.strokeStyle = dark;
    context.globalAlpha = .48;
    const ridgeCount = elevation === 2 ? 7 : 4;
    for (let ridge = 1; ridge <= ridgeCount; ridge += 1) {
      const ridgeX = x + (CELL_SIZE * ridge) / (ridgeCount + 1);
      const wobble = colorNoise(column + ridge, row - ridge) * .8;
      const ridgeBottom = faceY + faceDepth - 1 + Math.max(-3.5, Math.min(4.5,
        colorNoise(column * 5 + ridge, row * 7 + elevation * 3) * .72));
      context.beginPath();
      context.moveTo(ridgeX + wobble, faceY + 2);
      for (let segment = 1; segment <= 5; segment += 1) {
        const segmentY = segment === 5 ? ridgeBottom : faceY + (faceDepth * segment) / 5;
        const offset = colorNoise(column + ridge + segment, row + ridge) * .75;
        context.lineTo(ridgeX + offset, segmentY);
      }
      context.stroke();
    }
    context.globalAlpha = .18;
    for (let facet = 0; facet < ridgeCount - 1; facet += 1) {
      const facetX = x + 3 + ((column * 9 + row * 5 + facet * 13) % Math.max(8, CELL_SIZE - 8));
      const facetWidth = 3 + ((facet * 3 + column + row) % 5);
      const facetTop = faceY + 5 + ((row + facet * 7) % 7);
      const facetBottom = faceY + faceDepth - 4 - ((column + facet * 5) % 7);
      context.fillStyle = facet % 2 === 0 ? dark : highlight;
      context.beginPath();
      context.moveTo(facetX, facetTop);
      context.lineTo(facetX + facetWidth, facetTop + 2);
      context.lineTo(facetX + facetWidth * .65, facetBottom);
      context.lineTo(facetX - 1, facetBottom - 2);
      context.closePath();
      context.fill();
    }
  } else if (direction === "E") {
    context.beginPath();
    context.moveTo(faceX + faceDepth - 1, y); context.lineTo(faceX + faceDepth - 1, y + CELL_SIZE);
    context.stroke();
  } else {
    context.beginPath();
    context.moveTo(faceX + 1, y); context.lineTo(faceX + 1, y + CELL_SIZE);
    context.stroke();
  }
  context.restore();
}
function drawRaisedTile(column: number, row: number): void {
  const current = map.cells[cellIndex(map, column, row)];
  if (current.elevation === 0 || current.ground === "water") return;
  const south = row + 1 < map.rows ? map.cells[cellIndex(map, column, row + 1)] : null;
  const east = column + 1 < map.columns ? map.cells[cellIndex(map, column + 1, row)] : null;
  const west = column > 0 ? map.cells[cellIndex(map, column - 1, row)] : null;
  if (!south || south.elevation < current.elevation) {
    const exposedHeight = current.elevation - (south?.elevation ?? 0);
    drawRaisedTileFace(column, row, current.ground, exposedHeight as TileElevation, "S");
  }
  if (!east || east.elevation < current.elevation) drawRaisedTileFace(column, row, current.ground, current.elevation, "E");
  if (!west || west.elevation < current.elevation) drawRaisedTileFace(column, row, current.ground, current.elevation, "W");
}
function createTransitionCorrectionPath(column: number, row: number, mask: number): Path2D {
  const x = column * CELL_SIZE;
  const y = row * CELL_SIZE;
  const edge = CELL_SIZE * .25;
  const path = new Path2D();
  if (mask & NEIGHBOR_MASK.N) path.rect(x, y, CELL_SIZE, edge);
  if (mask & NEIGHBOR_MASK.E) path.rect(x + CELL_SIZE - edge, y, edge, CELL_SIZE);
  if (mask & NEIGHBOR_MASK.S) path.rect(x, y + CELL_SIZE - edge, CELL_SIZE, edge);
  if (mask & NEIGHBOR_MASK.W) path.rect(x, y, edge, CELL_SIZE);
  if (mask & NEIGHBOR_MASK.NE) {
    path.moveTo(x + CELL_SIZE, y); path.lineTo(x + CELL_SIZE, y + edge); path.lineTo(x + CELL_SIZE - edge, y); path.closePath();
  }
  if (mask & NEIGHBOR_MASK.SE) {
    path.moveTo(x + CELL_SIZE, y + CELL_SIZE); path.lineTo(x + CELL_SIZE - edge, y + CELL_SIZE); path.lineTo(x + CELL_SIZE, y + CELL_SIZE - edge); path.closePath();
  }
  if (mask & NEIGHBOR_MASK.SW) {
    path.moveTo(x, y + CELL_SIZE); path.lineTo(x, y + CELL_SIZE - edge); path.lineTo(x + edge, y + CELL_SIZE); path.closePath();
  }
  if (mask & NEIGHBOR_MASK.NW) {
    path.moveTo(x, y); path.lineTo(x + edge, y); path.lineTo(x, y + edge); path.closePath();
  }
  return path;
}
function drawGround(column: number, row: number, ground: GroundType): void {
  drawGroundTexture(column, row, ground);
}
function invalidateTransitionCorrectionCache(): void {
  transitionCorrectionCacheReady = false;
  transitionCorrectionBuildIndex = 0;
  transitionCorrectionBuildScheduled = false;
  transitionCorrectionContext.clearRect(0, 0, transitionCorrectionCanvas.width, transitionCorrectionCanvas.height);
  transitionCorrectionPatchCanvas.width = CELL_SIZE;
  transitionCorrectionPatchCanvas.height = CELL_SIZE;
  transitionCorrectionMaskCanvas.width = CELL_SIZE;
  transitionCorrectionMaskCanvas.height = CELL_SIZE;
  transitionCorrectionBlurCanvas.width = CELL_SIZE;
  transitionCorrectionBlurCanvas.height = CELL_SIZE;
}
function drawTransitionCorrectionToCache(
  ground: GroundType,
  targetColumn: number,
  targetRow: number,
  mask: number,
): void {
  const targetX = targetColumn * CELL_SIZE;
  const targetY = targetRow * CELL_SIZE;
  transitionCorrectionPatchContext.clearRect(0, 0, CELL_SIZE, CELL_SIZE);
  drawGroundTextureTo(transitionCorrectionPatchContext, targetColumn, targetRow, ground, 0, 0);

  transitionCorrectionMaskContext.clearRect(0, 0, CELL_SIZE, CELL_SIZE);
  transitionCorrectionMaskContext.save();
  transitionCorrectionMaskContext.translate(-targetX, -targetY);
  transitionCorrectionMaskContext.fillStyle = "#fff";
  transitionCorrectionMaskContext.fill(createTransitionCorrectionPath(targetColumn, targetRow, mask));
  transitionCorrectionMaskContext.restore();

  transitionCorrectionBlurContext.clearRect(0, 0, CELL_SIZE, CELL_SIZE);
  transitionCorrectionBlurContext.save();
  transitionCorrectionBlurContext.filter = `blur(${TRANSITION_CORRECTION_BLUR}px)`;
  transitionCorrectionBlurContext.drawImage(transitionCorrectionMaskCanvas, 0, 0);
  transitionCorrectionBlurContext.restore();

  transitionCorrectionPatchContext.save();
  transitionCorrectionPatchContext.globalCompositeOperation = "destination-in";
  transitionCorrectionPatchContext.drawImage(transitionCorrectionBlurCanvas, 0, 0);
  transitionCorrectionPatchContext.restore();

  const diagonalMask = NEIGHBOR_MASK.NE | NEIGHBOR_MASK.SE | NEIGHBOR_MASK.SW | NEIGHBOR_MASK.NW;
  const weight = mask & diagonalMask ? .78 : .92;
  transitionCorrectionContext.save();
  transitionCorrectionContext.globalAlpha = weight;
  transitionCorrectionContext.drawImage(transitionCorrectionPatchCanvas, targetX, targetY);
  transitionCorrectionContext.restore();
}
function scheduleTransitionCorrectionBuild(): void {
  if (transitionCorrectionCacheReady || transitionCorrectionBuildScheduled) return;
  transitionCorrectionBuildScheduled = true;
  window.setTimeout(processTransitionCorrectionBuild, 0);
}
function processTransitionCorrectionBuild(): void {
  transitionCorrectionBuildScheduled = false;
  const startedAt = performance.now();
  while (transitionCorrectionBuildIndex < map.cells.length && performance.now() - startedAt < 6) {
    const index = transitionCorrectionBuildIndex;
    const column = index % map.columns;
    const row = Math.floor(index / map.columns);
    for (const correction of getTransitionCorrections(map, column, row)) {
      drawTransitionCorrectionToCache(correction.ground, correction.targetColumn, correction.targetRow, correction.mask);
    }
    transitionCorrectionBuildIndex += 1;
  }
  if (transitionCorrectionBuildIndex < map.cells.length) {
    scheduleTransitionCorrectionBuild();
    return;
  }
  transitionCorrectionCacheReady = true;
  scheduleRender();
}
function drawGroundCorrections(): void {
  if (!transitionCorrectionCacheReady) {
    scheduleTransitionCorrectionBuild();
  } else {
    context.drawImage(transitionCorrectionCanvas, 0, 0);
  }
  for (let row = 0; row < map.rows; row += 1) for (let column = 0; column < map.columns; column += 1) {
    const ground = map.cells[cellIndex(map, column, row)].ground;
    const waterBankMask = getWaterBankMask(map, column, row);
    if (waterBankMask) drawWaterBank(column, row, ground, waterBankMask);
    const waterBankCornerMask = getWaterBankCornerMask(map, column, row);
    if (waterBankCornerMask) drawWaterBankCorners(column, row, ground, waterBankCornerMask);
  }
}
const brightnessCorrectionAlpha: Record<BrightnessCorrection, number> = {
  0: 0,
  1: .2,
  2: .32,
  3: .44,
};
const brightnessCorrectionLightnessShift: Record<BrightnessCorrection, number> = {
  0: 0,
  1: -.04,
  2: -.09,
  3: -.14,
};
const brightnessCorrectionSaturationBoost: Record<BrightnessCorrection, number> = {
  0: 0,
  1: .06,
  2: .12,
  3: .18,
};
const BRIGHTNESS_CORRECTION_BLUR = CELL_SIZE / 8;
type BrightnessCorrectionRect = { x: number; y: number; width: number; height: number };

function getBrightnessCorrectionColor(ground: GroundType, level: BrightnessCorrection): string {
  const key = `${ground},${level}`;
  const cached = brightnessCorrectionColorCache.get(key);
  if (cached) return cached;
  const [red, green, blue] = groundPalettes[ground];
  const maximum = Math.max(red, green, blue) / 255;
  const minimum = Math.min(red, green, blue) / 255;
  const lightness = (maximum + minimum) / 2;
  const difference = maximum - minimum;
  let hue = 0;
  let saturation = 0;
  if (difference > 0) {
    saturation = difference / (1 - Math.abs(2 * lightness - 1));
    if (maximum === red / 255) hue = ((green - blue) / 255 / difference) % 6;
    else if (maximum === green / 255) hue = (blue - red) / 255 / difference + 2;
    else hue = (red - green) / 255 / difference + 4;
    hue = Math.round(hue * 60);
    if (hue < 0) hue += 360;
  }
  const correctedLightness = Math.max(.08, lightness + brightnessCorrectionLightnessShift[level]);
  const correctedSaturation = Math.min(1, saturation + brightnessCorrectionSaturationBoost[level]);
  const color = `hsl(${hue} ${Math.round(correctedSaturation * 100)}% ${Math.round(correctedLightness * 100)}%)`;
  brightnessCorrectionColorCache.set(key, color);
  return color;
}

function invalidateBrightnessCorrectionCache(): void {
  brightnessCorrectionCacheReady = false;
  brightnessCorrectionActiveCellCount = 0;
  brightnessCorrectionFullBuild = null;
  brightnessCorrectionDirtyCells.clear();
  brightnessCorrectionPatchCanvas.width = 1;
  brightnessCorrectionPatchCanvas.height = 1;
}

function markBrightnessCorrectionChanges(
  previous: MapDocument,
  next: MapDocument,
  cells: CellPosition[],
): void {
  if (!brightnessCorrectionCacheReady) {
    if (brightnessCorrectionFullBuild) invalidateBrightnessCorrectionCache();
    return;
  }
  const changedIndexes = new Set<number>();
  for (const cell of cells) {
    if (cell.column < 0 || cell.column >= next.columns || cell.row < 0 || cell.row >= next.rows) continue;
    const index = cellIndex(next, cell.column, cell.row);
    if (changedIndexes.has(index)) continue;
    changedIndexes.add(index);
    const previousCell = previous.cells[index];
    const nextCell = next.cells[index];
    const previousLevel = previousCell?.brightnessCorrection ?? 0;
    const nextLevel = nextCell?.brightnessCorrection ?? 0;
    if (previousLevel === nextLevel && previousCell?.ground === nextCell?.ground) continue;
    brightnessCorrectionDirtyCells.add(index);
    if (previousLevel === 0 && nextLevel > 0) brightnessCorrectionActiveCellCount += 1;
    if (previousLevel > 0 && nextLevel === 0) brightnessCorrectionActiveCellCount -= 1;
  }
}

function getBrightnessCorrectionRect(column: number, row: number): BrightnessCorrectionRect {
  const x = Math.max(0, Math.floor(column * CELL_SIZE - BRIGHTNESS_CORRECTION_BLUR));
  const y = Math.max(0, Math.floor(row * CELL_SIZE - BRIGHTNESS_CORRECTION_BLUR));
  const right = Math.min(canvas.width, Math.ceil((column + 1) * CELL_SIZE + BRIGHTNESS_CORRECTION_BLUR));
  const bottom = Math.min(canvas.height, Math.ceil((row + 1) * CELL_SIZE + BRIGHTNESS_CORRECTION_BLUR));
  return { x, y, width: right - x, height: bottom - y };
}

function rectsOverlap(first: BrightnessCorrectionRect, second: BrightnessCorrectionRect): boolean {
  return first.x <= second.x + second.width
    && second.x <= first.x + first.width
    && first.y <= second.y + second.height
    && second.y <= first.y + first.height;
}

function mergeBrightnessCorrectionRects(rects: BrightnessCorrectionRect[]): BrightnessCorrectionRect[] {
  const merged: BrightnessCorrectionRect[] = [];
  for (const rect of rects) {
    let candidate = rect;
    let mergedAnother = true;
    while (mergedAnother) {
      mergedAnother = false;
      for (let index = 0; index < merged.length; index += 1) {
        const existing = merged[index];
        if (!rectsOverlap(existing, candidate)) continue;
        const right = Math.max(existing.x + existing.width, candidate.x + candidate.width);
        const bottom = Math.max(existing.y + existing.height, candidate.y + candidate.height);
        candidate = {
          x: Math.min(existing.x, candidate.x),
          y: Math.min(existing.y, candidate.y),
          width: right - Math.min(existing.x, candidate.x),
          height: bottom - Math.min(existing.y, candidate.y),
        };
        merged.splice(index, 1);
        mergedAnother = true;
        break;
      }
    }
    merged.push(candidate);
  }
  return merged;
}

function redrawBrightnessCorrectionRegion(region: BrightnessCorrectionRect): void {
  const patchX = Math.max(0, Math.floor(region.x - BRIGHTNESS_CORRECTION_BLUR));
  const patchY = Math.max(0, Math.floor(region.y - BRIGHTNESS_CORRECTION_BLUR));
  const patchRight = Math.min(canvas.width, Math.ceil(region.x + region.width + BRIGHTNESS_CORRECTION_BLUR));
  const patchBottom = Math.min(canvas.height, Math.ceil(region.y + region.height + BRIGHTNESS_CORRECTION_BLUR));
  brightnessCorrectionPatchCanvas.width = Math.max(1, patchRight - patchX);
  brightnessCorrectionPatchCanvas.height = Math.max(1, patchBottom - patchY);
  brightnessCorrectionPatchContext.imageSmoothingEnabled = false;
  brightnessCorrectionPatchContext.clearRect(0, 0, brightnessCorrectionPatchCanvas.width, brightnessCorrectionPatchCanvas.height);

  const firstColumn = Math.max(0, Math.floor(patchX / CELL_SIZE) - 1);
  const lastColumn = Math.min(map.columns - 1, Math.ceil(patchRight / CELL_SIZE));
  const firstRow = Math.max(0, Math.floor(patchY / CELL_SIZE) - 1);
  const lastRow = Math.min(map.rows - 1, Math.ceil(patchBottom / CELL_SIZE));
  for (let row = firstRow; row <= lastRow; row += 1) for (let column = firstColumn; column <= lastColumn; column += 1) {
    const cell = map.cells[cellIndex(map, column, row)];
    const level = cell.brightnessCorrection ?? 0;
    if (level === 0) continue;
    brightnessCorrectionPatchContext.fillStyle = getBrightnessCorrectionColor(cell.ground, level);
    brightnessCorrectionPatchContext.globalAlpha = brightnessCorrectionAlpha[level];
    brightnessCorrectionPatchContext.fillRect(
      column * CELL_SIZE - patchX,
      row * CELL_SIZE - patchY,
      CELL_SIZE,
      CELL_SIZE,
    );
  }
  brightnessCorrectionPatchContext.globalAlpha = 1;

  brightnessCorrectionContext.save();
  brightnessCorrectionContext.beginPath();
  brightnessCorrectionContext.rect(region.x, region.y, region.width, region.height);
  brightnessCorrectionContext.clip();
  brightnessCorrectionContext.clearRect(region.x, region.y, region.width, region.height);
  if (brightnessCorrectionActiveCellCount > 0) {
    brightnessCorrectionContext.filter = `blur(${BRIGHTNESS_CORRECTION_BLUR}px)`;
    brightnessCorrectionContext.drawImage(brightnessCorrectionPatchCanvas, patchX, patchY);
  }
  brightnessCorrectionContext.restore();
}

function scheduleBrightnessCorrectionBuild(): void {
  if (brightnessCorrectionBuildScheduled || !brightnessCorrectionFullBuild) return;
  brightnessCorrectionBuildScheduled = true;
  window.setTimeout(processBrightnessCorrectionBuild, 0);
}

function processBrightnessCorrectionBuild(): void {
  brightnessCorrectionBuildScheduled = false;
  const build = brightnessCorrectionFullBuild;
  if (!build) return;
  const startedAt = performance.now();
  while (build.nextRow < map.rows && performance.now() - startedAt < 6) {
    const row = build.nextRow;
    for (let column = 0; column < map.columns; column += 1) {
      const cell = map.cells[cellIndex(map, column, row)];
      const level = cell.brightnessCorrection ?? 0;
      if (level === 0) continue;
      build.activeCellCount += 1;
      brightnessCorrectionPatchContext.fillStyle = getBrightnessCorrectionColor(cell.ground, level);
      brightnessCorrectionPatchContext.globalAlpha = brightnessCorrectionAlpha[level];
      brightnessCorrectionPatchContext.fillRect(column * CELL_SIZE, row * CELL_SIZE, CELL_SIZE, CELL_SIZE);
    }
    build.nextRow += 1;
  }
  brightnessCorrectionPatchContext.globalAlpha = 1;
  if (build.nextRow < map.rows) {
    scheduleBrightnessCorrectionBuild();
    return;
  }
  brightnessCorrectionActiveCellCount = build.activeCellCount;
  brightnessCorrectionContext.clearRect(0, 0, canvas.width, canvas.height);
  brightnessCorrectionContext.save();
  brightnessCorrectionContext.filter = `blur(${BRIGHTNESS_CORRECTION_BLUR}px)`;
  brightnessCorrectionContext.drawImage(brightnessCorrectionPatchCanvas, 0, 0);
  brightnessCorrectionContext.restore();
  brightnessCorrectionCacheReady = true;
  brightnessCorrectionDirtyCells.clear();
  brightnessCorrectionFullBuild = null;
  brightnessCorrectionPatchCanvas.width = 1;
  brightnessCorrectionPatchCanvas.height = 1;
  scheduleRender();
}

function startBrightnessCorrectionBuild(): void {
  if (brightnessCorrectionCacheReady || brightnessCorrectionFullBuild) return;
  brightnessCorrectionPatchCanvas.width = canvas.width;
  brightnessCorrectionPatchCanvas.height = canvas.height;
  brightnessCorrectionPatchContext.imageSmoothingEnabled = false;
  brightnessCorrectionPatchContext.clearRect(0, 0, canvas.width, canvas.height);
  brightnessCorrectionFullBuild = { nextRow: 0, activeCellCount: 0 };
  scheduleBrightnessCorrectionBuild();
}

function refreshBrightnessCorrectionCache(): void {
  if (!brightnessCorrectionCacheReady) {
    startBrightnessCorrectionBuild();
    return;
  }
  if (brightnessCorrectionDirtyCells.size === 0) return;
  const regions = mergeBrightnessCorrectionRects(
    [...brightnessCorrectionDirtyCells].map((index) => getBrightnessCorrectionRect(index % map.columns, Math.floor(index / map.columns))),
  );
  for (const region of regions) redrawBrightnessCorrectionRegion(region);
  brightnessCorrectionDirtyCells.clear();
  brightnessCorrectionPatchCanvas.width = 1;
  brightnessCorrectionPatchCanvas.height = 1;
}

function drawBrightnessCorrections(): void {
  refreshBrightnessCorrectionCache();
  if (!brightnessCorrectionCacheReady || brightnessCorrectionActiveCellCount === 0) return;
  context.save();
  context.filter = "none";
  context.drawImage(brightnessCorrectionCanvas, 0, 0);
  context.restore();
}
function drawElevationHighlights(): void {
  if (selectedLayer !== "ground" || selectedGroundEditTab !== "elevation") return;
  context.save();
  context.fillStyle = "rgba(255, 207, 69, .22)";
  context.strokeStyle = "rgba(255, 184, 35, .98)";
  context.lineWidth = 2;
  for (let row = 0; row < map.rows; row += 1) for (let column = 0; column < map.columns; column += 1) {
    if (map.cells[cellIndex(map, column, row)].elevation === 0) continue;
    const x = column * CELL_SIZE;
    const y = row * CELL_SIZE;
    context.fillRect(x, y, CELL_SIZE, CELL_SIZE);
    context.strokeRect(x + 1, y + 1, CELL_SIZE - 2, CELL_SIZE - 2);
  }
  context.restore();
}
function drawShapePreview(): void {
  if (selectedLayer !== "ground" || brushMode !== "shape" || !shapeDrag?.current) return;
  const cells = getShapeCells(shapeDrag.start, shapeDrag.current, brushShape as "rectangle" | "ellipse");
  context.save();
  context.fillStyle = shapeDrag.erase ? "rgba(166, 81, 58, .22)" : "rgba(49, 90, 61, .23)";
  context.strokeStyle = shapeDrag.erase ? "rgba(166, 81, 58, .66)" : "rgba(49, 90, 61, .7)";
  context.lineWidth = 1.5;
  for (const cell of cells) {
    const x = cell.column * CELL_SIZE;
    const y = cell.row * CELL_SIZE;
    context.fillRect(x, y, CELL_SIZE, CELL_SIZE);
    context.strokeRect(x + 1, y + 1, CELL_SIZE - 2, CELL_SIZE - 2);
  }
  context.restore();
}
let renderScheduled = false;
function scheduleRender(): void {
  if (renderScheduled) return;
  renderScheduled = true;
  window.requestAnimationFrame(() => {
    renderScheduled = false;
    render();
  });
}
const propScale: Record<PropType, number> = {
  "broadleaf-tree": 2.15, "pine-tree": 2.05, shrub: 1.25, boulder: 1.35, "fallen-log": 1.65, footbridge: 1.5,
};
function drawProp(column: number, row: number, prop: PropType, opacity = 1): void {
  const image = propImages.get(prop);
  if (!image?.complete) return;
  const size = CELL_SIZE * propScale[prop];
  if (prop === "footbridge") {
    drawFootbridge(column, row, image, size, opacity);
    return;
  }
  context.save();
  context.globalAlpha = opacity;
  const centerY = row * CELL_SIZE + CELL_SIZE / 2;
  const imageY = prop === "shrub" ? centerY - size / 2 : row * CELL_SIZE + CELL_SIZE - size * 0.78;
  context.drawImage(image, column * CELL_SIZE + CELL_SIZE / 2 - size / 2, imageY, size, size);
  context.restore();
}
function createBridgeCellPath(column: number, row: number): Path2D {
  const path = new Path2D();
  path.rect(column * CELL_SIZE, row * CELL_SIZE, CELL_SIZE, CELL_SIZE);
  return path;
}
function createBridgeArmPath(column: number, row: number, direction: BridgeDirection): Path2D {
  const x = column * CELL_SIZE;
  const y = row * CELL_SIZE;
  const centerX = x + CELL_SIZE / 2;
  const centerY = y + CELL_SIZE / 2;
  const halfLane = CELL_SIZE * .28;
  const path = new Path2D();
  path.rect(centerX - halfLane, centerY - halfLane, halfLane * 2, halfLane * 2);
  if (direction === "N") path.rect(centerX - halfLane, y, halfLane * 2, CELL_SIZE / 2);
  if (direction === "E") path.rect(centerX, centerY - halfLane, CELL_SIZE / 2, halfLane * 2);
  if (direction === "S") path.rect(centerX - halfLane, centerY, halfLane * 2, CELL_SIZE / 2);
  if (direction === "W") path.rect(x, centerY - halfLane, CELL_SIZE / 2, halfLane * 2);
  return path;
}

function createBridgeConnectionPath(column: number, row: number, mask: number): Path2D {
  const x = column * CELL_SIZE;
  const y = row * CELL_SIZE;
  const centerX = x + CELL_SIZE / 2;
  const centerY = y + CELL_SIZE / 2;
  const halfLane = CELL_SIZE * .28;
  const path = new Path2D();
  if (mask === 255) {
    path.rect(x, y, CELL_SIZE, CELL_SIZE);
    return path;
  }
  path.rect(centerX - halfLane, centerY - halfLane, halfLane * 2, halfLane * 2);
  for (const direction of getBridgeConnectionDirections(mask)) {
    if (direction === "N") path.rect(centerX - halfLane, y, halfLane * 2, CELL_SIZE / 2);
    if (direction === "E") path.rect(centerX, centerY - halfLane, CELL_SIZE / 2, halfLane * 2);
    if (direction === "S") path.rect(centerX - halfLane, centerY, halfLane * 2, CELL_SIZE / 2);
    if (direction === "W") path.rect(x, centerY - halfLane, CELL_SIZE / 2, halfLane * 2);
  }
  const normalizedMask = mask & (NEIGHBOR_MASK.N | NEIGHBOR_MASK.E | NEIGHBOR_MASK.S | NEIGHBOR_MASK.W | NEIGHBOR_MASK.NE | NEIGHBOR_MASK.SE | NEIGHBOR_MASK.SW | NEIGHBOR_MASK.NW);
  if (normalizedMask & NEIGHBOR_MASK.NE) path.rect(centerX, y, CELL_SIZE / 2, CELL_SIZE / 2);
  if (normalizedMask & NEIGHBOR_MASK.SE) path.rect(centerX, centerY, CELL_SIZE / 2, CELL_SIZE / 2);
  if (normalizedMask & NEIGHBOR_MASK.SW) path.rect(x, centerY, CELL_SIZE / 2, CELL_SIZE / 2);
  if (normalizedMask & NEIGHBOR_MASK.NW) path.rect(x, y, CELL_SIZE / 2, CELL_SIZE / 2);
  return path;
}

function createBridgeDiagonalJoinPath(column: number, row: number, mask: number): Path2D | null {
  const x = column * CELL_SIZE;
  const y = row * CELL_SIZE;
  const centerX = x + CELL_SIZE / 2;
  const centerY = y + CELL_SIZE / 2;
  const path = new Path2D();
  let hasJoin = false;
  if (mask & NEIGHBOR_MASK.NE) { path.rect(centerX, y, CELL_SIZE / 2, CELL_SIZE / 2); hasJoin = true; }
  if (mask & NEIGHBOR_MASK.SE) { path.rect(centerX, centerY, CELL_SIZE / 2, CELL_SIZE / 2); hasJoin = true; }
  if (mask & NEIGHBOR_MASK.SW) { path.rect(x, centerY, CELL_SIZE / 2, CELL_SIZE / 2); hasJoin = true; }
  if (mask & NEIGHBOR_MASK.NW) { path.rect(x, y, CELL_SIZE / 2, CELL_SIZE / 2); hasJoin = true; }
  return hasJoin ? path : null;
}

function drawBridgeTexturePass(
  column: number,
  row: number,
  image: HTMLImageElement,
  size: number,
  opacity: number,
  direction: BridgeDirection | null,
  clipPath?: Path2D,
): void {
  const centerX = column * CELL_SIZE + CELL_SIZE / 2;
  const centerY = row * CELL_SIZE + CELL_SIZE - size * .78 + size / 2;
  const rotation = direction === "E" || direction === "W" ? Math.PI / 2 : 0;
  context.save();
  context.globalAlpha = opacity;
  context.clip(createBridgeCellPath(column, row));
  if (clipPath) context.clip(clipPath);
  context.translate(centerX, centerY);
  context.rotate(rotation);
  context.drawImage(image, -size / 2, -size / 2, size, size);
  context.restore();
}

function drawBridgeEndCap(column: number, row: number, direction: BridgeDirection, opacity: number): void {
  const x = column * CELL_SIZE;
  const y = row * CELL_SIZE;
  const inset = CELL_SIZE * .19;
  const laneWidth = CELL_SIZE - inset * 2;
  const depth = Math.max(3, CELL_SIZE * .13);
  const edge = direction === "N" || direction === "W" ? 1 : CELL_SIZE - depth - 1;
  context.save();
  context.globalAlpha = opacity;
  context.fillStyle = "rgba(38, 27, 18, .34)";
  if (direction === "N" || direction === "S") {
    context.fillRect(x + inset + 1, y + edge + 2, laneWidth, depth);
  } else {
    context.fillRect(x + edge + 2, y + inset + 1, depth, laneWidth);
  }
  context.fillStyle = "#70462c";
  context.strokeStyle = "#d19a5e";
  context.lineWidth = 1;
  if (direction === "N" || direction === "S") {
    context.fillRect(x + inset, y + edge, laneWidth, depth);
    context.strokeRect(x + inset + .5, y + edge + .5, laneWidth - 1, depth - 1);
    context.strokeStyle = "rgba(48, 29, 18, .75)";
    context.beginPath();
    context.moveTo(x + inset + 2, y + edge + depth * .52);
    context.lineTo(x + CELL_SIZE - inset - 2, y + edge + depth * .52);
    context.stroke();
  } else {
    context.fillRect(x + edge, y + inset, depth, laneWidth);
    context.strokeRect(x + edge + .5, y + inset + .5, depth - 1, laneWidth - 1);
    context.strokeStyle = "rgba(48, 29, 18, .75)";
    context.beginPath();
    context.moveTo(x + edge + depth * .52, y + inset + 2);
    context.lineTo(x + edge + depth * .52, y + CELL_SIZE - inset - 2);
    context.stroke();
  }
  context.restore();
}

function drawFootbridge(column: number, row: number, image: HTMLImageElement, size: number, opacity: number): void {
  const mask = getPropNeighborMask(map, column, row, "footbridge");
  const shape = getBridgeConnectionShape(mask);
  const directions = getBridgeConnectionDirections(mask);
  if (shape === "isolated" || shape === "full") {
    drawBridgeTexturePass(column, row, image, size, opacity, getBridgeTextureRotation(shape) === 90 ? "E" : "N");
  } else if (directions.length === 1 || shape === "horizontal" || shape === "vertical") {
    const direction = shape === "horizontal" ? "E" : shape === "vertical" ? "N" : directions[0];
    drawBridgeTexturePass(column, row, image, size, opacity, direction, createBridgeConnectionPath(column, row, mask));
  } else {
    for (const direction of directions) {
      drawBridgeTexturePass(column, row, image, size, opacity, direction, createBridgeArmPath(column, row, direction));
    }
    const diagonalJoinPath = createBridgeDiagonalJoinPath(column, row, mask);
    if (diagonalJoinPath) drawBridgeTexturePass(column, row, image, size, opacity, "N", diagonalJoinPath);
  }

  const endpointMask = getBridgeEndpointMask(mask);
  for (const [direction, bit] of [
    ["N", NEIGHBOR_MASK.N],
    ["E", NEIGHBOR_MASK.E],
    ["S", NEIGHBOR_MASK.S],
    ["W", NEIGHBOR_MASK.W],
  ] as const) {
    if ((endpointMask & bit) !== 0) drawBridgeEndCap(column, row, direction, opacity);
  }
}
function drawImagePlacement(placement: MapImagePlacement, opacity = 1, selected = false): void {
  const image = imageRenderImages.get(placement.imageId);
  if (!image?.complete || image.naturalWidth === 0 || image.naturalHeight === 0) return;
  const width = CELL_SIZE * placement.scale;
  const height = width * image.naturalHeight / image.naturalWidth;
  const centerX = placement.column * CELL_SIZE + CELL_SIZE / 2;
  const centerY = placement.row * CELL_SIZE + CELL_SIZE / 2;
  context.save();
  context.globalAlpha = opacity;
  context.translate(centerX, centerY);
  context.rotate(placement.rotation * Math.PI / 180);
  context.drawImage(image, -width / 2, -height / 2, width, height);
  if (selected) {
    context.strokeStyle = "rgba(199, 144, 78, .95)";
    context.lineWidth = 2;
    context.setLineDash([5, 4]);
    context.strokeRect(-width / 2, -height / 2, width, height);
    context.setLineDash([]);
  }
  context.restore();
}
function drawImagePlacementAnchors(): void {
  if (selectedLayer !== "image" || (imageMode !== "move" && imageMode !== "erase")) return;
  const isEraseMode = imageMode === "erase";
  const anchorDark = isEraseMode ? "rgba(100, 35, 24, .94)" : "rgba(72, 45, 10, .94)";
  const anchorBright = isEraseMode ? "#ff8068" : "#ffc044";
  const anchorFill = isEraseMode ? "rgba(221, 71, 48, .34)" : "rgba(247, 168, 25, .34)";
  context.save();
  for (const placement of map.images) {
    const centerX = placement.column * CELL_SIZE + CELL_SIZE / 2;
    const centerY = placement.row * CELL_SIZE + CELL_SIZE / 2;
    const radius = CELL_SIZE * .27;
    context.beginPath();
    context.fillStyle = anchorFill;
    context.strokeStyle = anchorDark;
    context.lineWidth = 4;
    context.arc(centerX, centerY, radius, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.beginPath();
    context.strokeStyle = anchorBright;
    context.lineWidth = 2;
    context.arc(centerX, centerY, radius - 2, 0, Math.PI * 2);
    context.stroke();
    context.beginPath();
    context.fillStyle = anchorBright;
    context.arc(centerX, centerY, CELL_SIZE * .065, 0, Math.PI * 2);
    context.fill();
  }
  context.restore();
}
function render(): void {
  waterDepths = getWaterDepths(map);
  context.clearRect(0, 0, canvas.width, canvas.height);
  for (let row = 0; row < map.rows; row += 1) for (let column = 0; column < map.columns; column += 1) {
    drawGround(column, row, map.cells[cellIndex(map, column, row)].ground);
  }
  drawGroundCorrections();
  drawBrightnessCorrections();
  for (let row = 0; row < map.rows; row += 1) for (let column = 0; column < map.columns; column += 1) {
    drawRaisedTile(column, row);
  }
  drawElevationHighlights();
  drawShapePreview();
  for (let row = 0; row < map.rows; row += 1) for (let column = 0; column < map.columns; column += 1) {
    const prop = map.cells[cellIndex(map, column, row)].prop;
    if (!prop) continue;
    if (movingProp && movingProp.fromColumn === column && movingProp.fromRow === row) continue;
    drawProp(column, row, prop);
  }
  map.images.forEach((image, index) => {
    if (movingImage?.index === index) return;
    drawImagePlacement(image, 1, selectedLayer === "image" && selectedImagePlacementIndex === index);
  });
  if (movingProp?.target) {
    drawProp(movingProp.target.column, movingProp.target.row, movingProp.prop, .62);
  }
  if (movingImage?.target) {
    const image = map.images[movingImage.index];
    if (image) {
      drawImagePlacement({ ...image, column: movingImage.target.column, row: movingImage.target.row }, .62, true);
    }
  }
  drawImagePlacementAnchors();
  if (gridVisible) {
    context.save(); context.strokeStyle = "rgba(21,45,29,.17)"; context.lineWidth = 1; context.beginPath();
    for (let column = 0; column <= map.columns; column += 1) { context.moveTo(column * CELL_SIZE + .5, 0); context.lineTo(column * CELL_SIZE + .5, canvas.height); }
    for (let row = 0; row <= map.rows; row += 1) { context.moveTo(0, row * CELL_SIZE + .5); context.lineTo(canvas.width, row * CELL_SIZE + .5); }
    context.stroke(); context.restore();
  }
  updateHistoryButtons();
}
const MIN_ZOOM = .125;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = .25;
const WHEEL_ZOOM_FACTOR = 1.1;
type TouchPoint = { x: number; y: number };
const touchPointers = new Map<number, TouchPoint>();
let touchGestureLocked = false;
let isPinching = false;
let pinchStartDistance = 0;
let pinchStartMidpoint: TouchPoint = { x: 0, y: 0 };
let pinchStartZoom = 1;
let pinchStartPanX = 0;
let pinchStartPanY = 0;
let touchStartMap: MapDocument | null = null;
let touchStartHistoryLength = 0;
let touchStartFuture: MapDocument[] = [];
let touchStartPanX = 0;
let touchStartPanY = 0;
let touchStartSelectedImageId: string | null = null;
let touchStartSelectedImagePlacementIndex: number | null = null;
let touchStartSelectedImageRotation = DEFAULT_IMAGE_ROTATION;
let touchStartSelectedImageScale = DEFAULT_IMAGE_SCALE;

function getTouchPair(): [TouchPoint, TouchPoint] | null {
  const points = [...touchPointers.values()];
  return points.length >= 2 ? [points[0], points[1]] : null;
}
function getTouchDistance(first: TouchPoint, second: TouchPoint): number {
  return Math.hypot(second.x - first.x, second.y - first.y);
}
function getTouchMidpoint(first: TouchPoint, second: TouchPoint): TouchPoint {
  return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
}
function captureTouchStartState(): void {
  touchStartMap = cloneMap(map);
  touchStartHistoryLength = history.length;
  touchStartFuture = future.slice();
  touchStartPanX = panX;
  touchStartPanY = panY;
  touchStartSelectedImageId = selectedImageId;
  touchStartSelectedImagePlacementIndex = selectedImagePlacementIndex;
  touchStartSelectedImageRotation = selectedImageRotation;
  touchStartSelectedImageScale = selectedImageScale;
}
function cancelTouchEditing(): void {
  if (!touchStartMap) return;
  map = cloneMap(touchStartMap);
  history.length = touchStartHistoryLength;
  future = touchStartFuture.slice();
  panX = touchStartPanX;
  panY = touchStartPanY;
  selectedImageId = touchStartSelectedImageId;
  selectedImagePlacementIndex = touchStartSelectedImagePlacementIndex;
  selectedImageRotation = touchStartSelectedImageRotation;
  selectedImageScale = touchStartSelectedImageScale;
  movingProp = null;
  movingImage = null;
  imageTransformChanged = false;
  isDrawing = false;
  strokeChanged = false;
  lastPaintedCell = "";
  syncImageTransformControls();
  renderImageMaterials();
  render();
}
function beginPinch(): void {
  const pair = getTouchPair();
  if (!pair) return;
  cancelTouchEditing();
  const [first, second] = pair;
  isPinching = true;
  touchGestureLocked = true;
  pinchStartDistance = Math.max(getTouchDistance(first, second), 1);
  pinchStartMidpoint = getTouchMidpoint(first, second);
  pinchStartZoom = zoom;
  pinchStartPanX = panX;
  pinchStartPanY = panY;
  canvas.setPointerCapture?.([...touchPointers.keys()][1]);
}
function updatePinch(): void {
  if (!isPinching) return;
  const pair = getTouchPair();
  if (!pair) return;
  const [first, second] = pair;
  const midpoint = getTouchMidpoint(first, second);
  const nextZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinchStartZoom * getTouchDistance(first, second) / pinchStartDistance));
  const viewport = canvasScroll.getBoundingClientRect();
  const viewportCenterX = viewport.left + viewport.width / 2;
  const viewportCenterY = viewport.top + viewport.height / 2;
  const startFocusX = pinchStartMidpoint.x - viewportCenterX;
  const startFocusY = pinchStartMidpoint.y - viewportCenterY;
  const currentFocusX = midpoint.x - viewportCenterX;
  const currentFocusY = midpoint.y - viewportCenterY;
  const ratio = nextZoom / pinchStartZoom;
  zoom = nextZoom;
  panX = currentFocusX - (startFocusX - pinchStartPanX) * ratio;
  panY = currentFocusY - (startFocusY - pinchStartPanY) * ratio;
  updateViewport();
}
function trackTouchStart(event: PointerEvent): boolean {
  if (event.pointerType !== "touch") return false;
  if (touchPointers.size === 0) captureTouchStartState();
  touchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
  if (touchPointers.size >= 2 && !isPinching) beginPinch();
  return isPinching || touchGestureLocked;
}
function trackTouchMove(event: PointerEvent): boolean {
  if (event.pointerType !== "touch" || !touchPointers.has(event.pointerId)) return false;
  touchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
  return isPinching || touchGestureLocked;
}
function trackTouchEnd(event: PointerEvent): boolean {
  if (event.pointerType !== "touch" || !touchPointers.has(event.pointerId)) return false;
  touchPointers.delete(event.pointerId);
  const handled = isPinching || touchGestureLocked;
  if (touchPointers.size < 2) isPinching = false;
  if (touchPointers.size === 0) {
    touchGestureLocked = false;
    touchStartMap = null;
  }
  return handled;
}
function updateViewport(): void {
  canvasFrame.style.setProperty("--map-zoom", String(zoom));
  canvasFrame.style.setProperty("--pan-x", `${panX}px`);
  canvasFrame.style.setProperty("--pan-y", `${panY}px`);
  const readout = document.querySelector<HTMLButtonElement>("#zoom-reset")!;
  readout.textContent = `${Math.round(zoom * 100)}%`;
  readout.setAttribute("aria-label", `확대/축소 ${Math.round(zoom * 100)}%, 클릭하면 위치와 함께 초기화`);
  canvas.classList.toggle("pan-mode", panMode || spacePressed || isPanning);
  canvas.classList.toggle("is-panning", isPanning);
  document.querySelector<HTMLButtonElement>("#toggle-pan")!.classList.toggle("active", panMode);
  document.querySelector<HTMLButtonElement>("#toggle-pan")!.setAttribute("aria-pressed", String(panMode));
}
function setZoom(nextZoom: number, focus?: { x: number; y: number }): void {
  const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nextZoom));
  if (next === zoom) return;
  if (focus) {
    const viewport = canvasScroll.getBoundingClientRect();
    const focusX = focus.x - (viewport.left + viewport.width / 2);
    const focusY = focus.y - (viewport.top + viewport.height / 2);
    const ratio = next / zoom;
    panX = focusX - (focusX - panX) * ratio;
    panY = focusY - (focusY - panY) * ratio;
  }
  zoom = next;
  updateViewport();
}
function resetViewport(): void {
  zoom = 1;
  panX = 0;
  panY = 0;
  updateViewport();
}
function beginPan(event: PointerEvent): void {
  isPanning = true;
  panStartX = event.clientX;
  panStartY = event.clientY;
  panOriginX = panX;
  panOriginY = panY;
  canvas.setPointerCapture(event.pointerId);
  updateViewport();
}
function panAt(event: PointerEvent): void {
  if (!isPanning) return;
  panX = panOriginX + event.clientX - panStartX;
  panY = panOriginY + event.clientY - panStartY;
  updateViewport();
}
function getCell(event: PointerEvent): CellPosition | null {
  const rect = canvas.getBoundingClientRect();
  if (event.clientX < rect.left || event.clientX >= rect.right || event.clientY < rect.top || event.clientY >= rect.bottom) return null;
  const scaleX = rect.width / canvas.width;
  const scaleY = rect.height / canvas.height;
  const column = Math.floor((event.clientX - rect.left) / scaleX / CELL_SIZE);
  const row = Math.floor((event.clientY - rect.top) / scaleY / CELL_SIZE);
  return column >= 0 && row >= 0 && column < map.columns && row < map.rows ? { column, row } : null;
}
function setBrushSize(value: number): void {
  const next = Number.isFinite(value) ? Math.max(1, Math.min(MAX_BRUSH_SIZE, Math.round(value))) : brushSize;
  brushSize = next;
  const range = document.querySelector<HTMLInputElement>("#brush-size-range");
  const number = document.querySelector<HTMLInputElement>("#brush-size-number");
  const output = document.querySelector<HTMLOutputElement>("#brush-size-output");
  if (range) range.value = String(next);
  if (number) number.value = String(next);
  if (output) output.value = `${next} × ${next}`;
}
function updateBrushShapeButtons(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-brush-shape]").forEach((button) => {
    const active = button.dataset.brushShape === brushShape;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  });
}
function setBrushMode(mode: BrushMode): void {
  brushMode = mode;
  if (mode === "brush" && (brushShape === "rectangle" || brushShape === "ellipse")) brushShape = "square";
  if (mode === "shape" && (brushShape === "square" || brushShape === "circle")) brushShape = "rectangle";
  document.querySelectorAll<HTMLButtonElement>("[data-brush-mode]").forEach((button) => {
    const active = button.dataset.brushMode === mode;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  });
  document.querySelector("#brush-shapes")?.classList.toggle("hidden", mode !== "brush");
  document.querySelector("#fill-shapes")?.classList.toggle("hidden", mode !== "shape");
  const hint = document.querySelector("#brush-size-hint");
  if (hint) hint.textContent = mode === "shape"
    ? "Drag across the map to fill a rectangle or ellipse."
    : "Paint a square or circle from 1 × 1 up to 10 × 10 cells.";
  updateBrushShapeButtons();
  shapeDrag = null;
  render();
}
function setBrushShape(shape: BrushShape): void {
  const valid = brushMode === "brush"
    ? shape === "square" || shape === "circle"
    : shape === "rectangle" || shape === "ellipse";
  if (!valid) return;
  brushShape = shape;
  updateBrushShapeButtons();
  render();
}
function getBrushCells(column: number, row: number): CellPosition[] {
  const cells: CellPosition[] = [];
  const startColumn = column - Math.floor(brushSize / 2);
  const startRow = row - Math.floor(brushSize / 2);
  const center = (brushSize - 1) / 2;
  const radius = brushSize / 2;
  for (let brushRow = 0; brushRow < brushSize; brushRow += 1) for (let brushColumn = 0; brushColumn < brushSize; brushColumn += 1) {
    if (brushShape === "circle") {
      const distanceX = brushColumn - center;
      const distanceY = brushRow - center;
      if (distanceX * distanceX + distanceY * distanceY > radius * radius) continue;
    }
    const targetColumn = startColumn + brushColumn;
    const targetRow = startRow + brushRow;
    if (targetColumn >= 0 && targetColumn < map.columns && targetRow >= 0 && targetRow < map.rows) {
      cells.push({ column: targetColumn, row: targetRow });
    }
  }
  return cells;
}
function getShapeCells(start: CellPosition, current: CellPosition, shape: "rectangle" | "ellipse"): CellPosition[] {
  const left = Math.min(start.column, current.column);
  const right = Math.max(start.column, current.column);
  const top = Math.min(start.row, current.row);
  const bottom = Math.max(start.row, current.row);
  const width = right - left + 1;
  const height = bottom - top + 1;
  const centerColumn = (left + right + 1) / 2;
  const centerRow = (top + bottom + 1) / 2;
  const radiusColumn = width / 2;
  const radiusRow = height / 2;
  const cells: CellPosition[] = [];
  for (let row = top; row <= bottom; row += 1) for (let column = left; column <= right; column += 1) {
    if (shape === "ellipse") {
      const distanceColumn = (column + .5 - centerColumn) / radiusColumn;
      const distanceRow = (row + .5 - centerRow) / radiusRow;
      if (distanceColumn * distanceColumn + distanceRow * distanceRow > 1) continue;
    }
    if (column >= 0 && column < map.columns && row >= 0 && row < map.rows) cells.push({ column, row });
  }
  return cells;
}
function applyGroundCells(candidate: MapDocument, cells: CellPosition[], erase: boolean): boolean {
  let changed = false;
  for (const target of cells) {
    const nextChanged = selectedGroundEditTab === "elevation"
      ? setTileElevation(candidate, target.column, target.row, erase ? 0 : selectedTileElevation)
      : paintGround(
        candidate,
        target.column,
        target.row,
        erase ? "grass" : selectedGround,
        erase ? 0 : selectedGroundBrightness,
      );
    changed = nextChanged || changed;
  }
  return changed;
}
function getTopImageIndex(column: number, row: number): number {
  for (let index = map.images.length - 1; index >= 0; index -= 1) {
    const image = map.images[index];
    if (image.column === column && image.row === row) return index;
  }
  return -1;
}
function selectImagePlacement(index: number): void {
  const image = map.images[index];
  if (!image) return;
  selectedImagePlacementIndex = index;
  selectedImageId = image.imageId;
  selectedImageRotation = image.rotation;
  selectedImageScale = image.scale;
  syncImageTransformControls();
  renderImageMaterials();
  render();
}
function paintAt(event: PointerEvent): void {
  if (selectedLayer === "image" && imageMode === "place" && !imageContinuousPlacement
    && (!isDrawing || event.type === "pointermove")) return;
  const cell = getCell(event);
  const key = cell ? `${cell.column}:${cell.row}` : "outside";
  if (key === lastPaintedCell) return;
  lastPaintedCell = key;
  if (!cell) {
    if (movingProp) {
      movingProp.target = null;
      render();
    }
    if (shapeDrag) {
      shapeDrag.current = null;
      render();
    }
    return;
  }
  const { column, row } = cell;
  document.querySelector("#cursor-status")!.textContent = `열 ${column + 1} · 행 ${row + 1}`;
  if (!isDrawing) return;
  if (selectedLayer === "ground" && brushMode === "shape" && shapeDrag) {
    shapeDrag.current = cell;
    render();
    return;
  }
  if (selectedLayer === "prop" && propMode === "move" && movingProp) {
    movingProp.target = cell;
    render();
    return;
  }
  if (selectedLayer === "image" && imageMode === "move" && movingImage) {
    movingImage.target = cell;
    render();
    return;
  }
  const erase = (selectedLayer === "prop" && propMode === "erase") || event.button === 2 || (event.buttons & 2) === 2;
  if (selectedLayer === "image") {
    const candidate = cloneMap(map);
    const imageIndex = getTopImageIndex(column, row);
    const changed = imageMode === "erase" || erase
      ? imageIndex >= 0 && removeImage(candidate, imageIndex)
      : selectedImageId !== null && placeImage(candidate, selectedImageId, column, row, selectedImageRotation, selectedImageScale);
    if (!changed) return;
    if (!strokeChanged) { history.push(cloneMap(map)); if (history.length > 60) history.shift(); future = []; }
    strokeChanged = true;
    map = candidate;
    if (imageMode === "place" && selectedImageId) {
      selectedImagePlacementIndex = map.images.length - 1;
      syncImageTransformControls();
    } else if (imageIndex >= 0 && selectedImagePlacementIndex !== null) {
      if (selectedImagePlacementIndex === imageIndex) selectedImagePlacementIndex = null;
      else if (selectedImagePlacementIndex > imageIndex) selectedImagePlacementIndex -= 1;
      syncImageTransformControls();
    }
    render();
    return;
  }
  const candidate = cloneMap(map);
  const groundCells = selectedLayer === "ground" ? getBrushCells(column, row) : [];
  const changed = selectedLayer === "ground"
    ? applyGroundCells(candidate, groundCells, erase)
    : placeProp(candidate, column, row, erase ? null : selectedProp);
  if (!changed) return;
  if (!strokeChanged) { history.push(cloneMap(map)); if (history.length > 60) history.shift(); future = []; }
  if (selectedLayer === "ground") markBrightnessCorrectionChanges(map, candidate, groundCells);
  if (selectedLayer === "ground") invalidateTransitionCorrectionCache();
  strokeChanged = true; map = candidate; render();
}
function finishStroke(): void {
  if (isPanning) {
    isPanning = false;
    isDrawing = false;
    lastPaintedCell = "";
    updateViewport();
    return;
  }
  if (shapeDrag) {
    const drag = shapeDrag;
    shapeDrag = null;
    if (drag.current) {
      const candidate = cloneMap(map);
      const groundCells = getShapeCells(drag.start, drag.current, brushShape as "rectangle" | "ellipse");
      const changed = applyGroundCells(candidate, groundCells, drag.erase);
      if (changed) {
        history.push(cloneMap(map));
        if (history.length > 60) history.shift();
        future = [];
        strokeChanged = true;
        invalidateTransitionCorrectionCache();
        markBrightnessCorrectionChanges(map, candidate, groundCells);
        map = candidate;
      }
    }
    render();
    if (strokeChanged) scheduleSave();
    isDrawing = false;
    strokeChanged = false;
    lastPaintedCell = "";
    return;
  }
  if (movingProp) {
    const move = movingProp;
    if (move.target) {
      const candidate = cloneMap(map);
      const changed = moveProp(candidate, move.fromColumn, move.fromRow, move.target.column, move.target.row);
      if (changed) {
        history.push(cloneMap(map));
        if (history.length > 60) history.shift();
        future = [];
        strokeChanged = true;
        map = candidate;
      }
    }
    movingProp = null;
    render();
  }
  if (movingImage) {
    const move = movingImage;
    if (move.target) {
      const candidate = cloneMap(map);
      const changed = moveImage(candidate, move.index, move.target.column, move.target.row);
      if (changed) {
        history.push(cloneMap(map));
        if (history.length > 60) history.shift();
        future = [];
        strokeChanged = true;
        map = candidate;
      }
    }
    selectedImagePlacementIndex = move.index;
    movingImage = null;
    syncImageTransformControls();
    render();
  }
  if (strokeChanged) scheduleSave();
  isDrawing = false; strokeChanged = false; lastPaintedCell = "";
}
function scheduleSave(): void {
  const status = document.querySelector("#save-status")!;
  status.textContent = "저장 중…"; window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => { localStorage.setItem(STORAGE_KEY, serializeMap(map)); status.textContent = "브라우저에 자동 저장됨"; }, 260);
}
function updateHistoryButtons(): void {
  (document.querySelector("#undo") as HTMLButtonElement).disabled = history.length === 0;
  (document.querySelector("#redo") as HTMLButtonElement).disabled = future.length === 0;
}
function syncName(): void { (document.querySelector("#map-name") as HTMLInputElement).value = map.name; }
function undo(): void {
  const previous = history.pop(); if (!previous) return;
  future.push(cloneMap(map)); map = previous; syncCanvasSize(); syncName(); render(); scheduleSave();
}
function redo(): void {
  const next = future.pop(); if (!next) return;
  history.push(cloneMap(map)); map = next; syncCanvasSize(); syncName(); render(); scheduleSave();
}
function replaceMap(next: MapDocument): void {
  history.push(cloneMap(map)); future = []; map = next; syncCanvasSize(); syncName(); render(); scheduleSave();
}
function download(content: Blob, filename: string): void {
  const link = document.createElement("a"); link.href = URL.createObjectURL(content); link.download = filename; link.click(); URL.revokeObjectURL(link.href);
}
function setFileMenuOpen(open: boolean): void {
  fileMenuOpen = open;
  document.querySelector("#file-menu")!.classList.toggle("hidden", !open);
  document.querySelector("#file-menu-toggle")!.setAttribute("aria-expanded", String(open));
}
function setDialogMessage(selector: string, message: string, kind: "error" | "success" | "" = ""): void {
  const element = document.querySelector<HTMLElement>(selector)!;
  element.textContent = message;
  element.classList.toggle("is-error", kind === "error");
  element.classList.toggle("is-success", kind === "success");
}
function openMapSaveDialog(asNew = false): void {
  setFileMenuOpen(false);
  saveAsMode = asNew;
  const dialog = document.querySelector<HTMLDialogElement>("#map-save-dialog")!;
  const input = document.querySelector<HTMLInputElement>("#map-save-name")!;
  const button = document.querySelector<HTMLButtonElement>("#confirm-map-save")!;
  input.value = map.name;
  document.querySelector("#map-save-title")!.textContent = asNew ? "다른 이름으로 저장" : "지도 저장";
  button.disabled = !authSession || !mapStorageClient;
  setDialogMessage("#map-save-message", authSession ? "" : "DB 저장은 로그인이 필요합니다.");
  dialog.showModal();
}
async function saveMapToCloud(showDialog = true): Promise<void> {
  const button = document.querySelector<HTMLButtonElement>("#confirm-map-save")!;
  if (!authSession || !mapStorageClient) {
    if (showDialog) setDialogMessage("#map-save-message", "DB 저장은 로그인이 필요합니다.", "error");
    return;
  }
  const name = showDialog
    ? document.querySelector<HTMLInputElement>("#map-save-name")!.value.trim()
    : map.name.trim();
  if (!name) {
    if (showDialog) setDialogMessage("#map-save-message", "지도 이름을 입력해 주세요.", "error");
    return;
  }
  if (showDialog) {
    button.disabled = true;
    setDialogMessage("#map-save-message", "저장 중입니다.");
  } else {
    document.querySelector("#save-status")!.textContent = "Saving map...";
  }
  try {
    const saved = saveAsMode || !savedMapId
      ? await mapStorageClient.saveMap(authSession.token, map, name)
      : await mapStorageClient.updateMap(authSession.token, savedMapId, map, name);
    savedMapId = saved.id;
    saveAsMode = false;
    map.name = saved.name;
    syncName();
    scheduleSave();
    if (showDialog) {
      setDialogMessage("#map-save-message", "개인 지도 목록에 저장했습니다.", "success");
      window.setTimeout(() => document.querySelector<HTMLDialogElement>("#map-save-dialog")!.close(), 450);
    } else {
      document.querySelector("#save-status")!.textContent = "Map saved.";
    }
  } catch (error) {
    if (showDialog) {
      setDialogMessage("#map-save-message", error instanceof Error ? error.message : "지도를 저장하지 못했습니다.", "error");
    } else {
      document.querySelector("#save-status")!.textContent = error instanceof Error ? error.message : "Map save failed.";
    }
    if (showDialog) showMapDebugDialog(error);
  } finally {
    if (showDialog) button.disabled = false;
  }
}
function renderMapLibrary(items: readonly { id: string; name: string; createdAt?: string; updatedAt?: string }[]): void {
  const list = document.querySelector<HTMLDivElement>("#map-library-list")!;
  list.replaceChildren();
  if (items.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty-library";
    empty.textContent = "저장한 지도가 없습니다.";
    list.append(empty);
    return;
  }
  for (const item of items) {
    const row = document.createElement("div");
    row.className = "map-library-item";
    const detail = document.createElement("div");
    const name = document.createElement("strong");
    name.textContent = item.name;
    const date = document.createElement("small");
    date.textContent = item.updatedAt ?? item.createdAt ?? "";
    detail.append(name, date);
    const load = document.createElement("button");
    load.type = "button";
    load.className = "button export";
    load.textContent = "불러오기";
    load.addEventListener("click", () => { void loadSavedMap(item.id, load); });
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "button danger";
    remove.textContent = "삭제";
    remove.addEventListener("click", () => { void deleteSavedMap(item.id, remove); });
    row.append(detail, load, remove);
    list.append(row);
  }
}
async function loadSavedMap(id: string, button: HTMLButtonElement): Promise<void> {
  if (!authSession || !mapStorageClient) return;
  button.disabled = true;
  setDialogMessage("#map-library-message", "지도를 불러오는 중입니다.");
  try {
    const loaded = await mapStorageClient.loadMap(authSession.token, id);
    savedMapId = id;
    replaceMap(loaded);
    document.querySelector<HTMLDialogElement>("#map-library-dialog")!.close();
  } catch (error) {
    setDialogMessage("#map-library-message", error instanceof Error ? error.message : "지도를 불러오지 못했습니다.", "error");
  } finally {
    button.disabled = false;
  }
}
async function deleteSavedMap(id: string, button: HTMLButtonElement): Promise<void> {
  if (!authSession || !mapStorageClient) return;
  if (!window.confirm("이 지도를 DB 목록에서 삭제할까요?")) return;
  button.disabled = true;
  setDialogMessage("#map-library-message", "지도를 삭제하는 중입니다.");
  try {
    await mapStorageClient.deleteMap(authSession.token, id);
    if (savedMapId === id) savedMapId = null;
    const items = await mapStorageClient.listMaps(authSession.token);
    renderMapLibrary(items);
    setDialogMessage("#map-library-message", items.length ? "삭제했습니다. 지도를 선택해 주세요." : "저장한 지도가 없습니다.", "success");
  } catch (error) {
    setDialogMessage("#map-library-message", error instanceof Error ? error.message : "지도를 삭제하지 못했습니다.", "error");
  } finally {
    button.disabled = false;
  }
}
async function openMapLibrary(): Promise<void> {
  setFileMenuOpen(false);
  const dialog = document.querySelector<HTMLDialogElement>("#map-library-dialog")!;
  const list = document.querySelector<HTMLDivElement>("#map-library-list")!;
  list.replaceChildren();
  if (!authSession || !mapStorageClient) {
    setDialogMessage("#map-library-message", "DB 저장과 목록 조회는 로그인이 필요합니다.", "error");
    dialog.showModal();
    return;
  }
  setDialogMessage("#map-library-message", "저장한 지도를 불러오는 중입니다.");
  dialog.showModal();
  try {
    const items = await mapStorageClient.listMaps(authSession.token);
    setDialogMessage("#map-library-message", items.length ? "불러올 지도를 선택하세요." : "");
    renderMapLibrary(items);
  } catch (error) {
    setDialogMessage("#map-library-message", error instanceof Error ? error.message : "지도 목록을 불러오지 못했습니다.", "error");
  }
}

let adminSelectedUserId: string | null = null;

function setAdminMenuVisible(visible: boolean): void {
  adminAccess = visible;
  document.querySelector<HTMLElement>("#admin-menu-section")?.classList.toggle("hidden", !visible);
}

function renderAdminUserList(users: readonly AdminUser[]): void {
  const list = document.querySelector<HTMLDivElement>("#admin-user-list");
  if (!list) return;
  list.replaceChildren();
  if (users.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty-library";
    empty.textContent = "등록된 사용자가 없습니다.";
    list.append(empty);
    return;
  }
  for (const user of users) {
    const row = document.createElement("div");
    row.className = "admin-user-item";
    row.dataset.userId = user.id;
    row.classList.toggle("selected", adminSelectedUserId === user.id);
    const select = document.createElement("button");
    select.type = "button";
    select.className = "admin-user-select";
    select.innerHTML = `<strong></strong><small></small>`;
    select.querySelector("strong")!.textContent = user.displayName;
    select.querySelector("small")!.textContent = user.email;
    select.addEventListener("click", () => { void loadAdminUserMaps(user); });
    row.append(select);
    if (!user.isAdmin) {
      const promote = document.createElement("button");
      promote.type = "button";
      promote.className = "button export admin-promote";
      promote.textContent = "관리자 등록";
      promote.addEventListener("click", () => { void promoteAdminUser(user); });
      row.append(promote);
    } else {
      const badge = document.createElement("span");
      badge.className = "admin-badge";
      badge.textContent = "관리자";
      row.append(badge);
    }
    list.append(row);
  }
}

function renderAdminMapList(user: AdminUser, maps: readonly AdminMapSummary[]): void {
  const title = document.querySelector<HTMLElement>("#admin-maps-title");
  const list = document.querySelector<HTMLDivElement>("#admin-map-list");
  if (!list) return;
  if (title) title.textContent = `${user.displayName}의 저장된 맵`;
  list.replaceChildren();
  if (maps.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty-library";
    empty.textContent = "저장된 맵이 없습니다.";
    list.append(empty);
    return;
  }
  for (const item of maps) {
    const row = document.createElement("div");
    row.className = "admin-map-item";
    const detail = document.createElement("div");
    const name = document.createElement("strong");
    name.textContent = item.name;
    const date = document.createElement("small");
    date.textContent = item.updatedAt ?? item.createdAt ?? "";
    detail.append(name, date);
    const load = document.createElement("button");
    load.type = "button";
    load.className = "button export";
    load.textContent = "맵 열기";
    load.addEventListener("click", () => { void loadAdminMap(user.id, item.id, load); });
    row.append(detail, load);
    list.append(row);
  }
}

async function loadAdminUserMaps(user: AdminUser): Promise<void> {
  if (!adminClient) return;
  adminSelectedUserId = user.id;
  document.querySelectorAll<HTMLElement>(".admin-user-item").forEach((item) => item.classList.remove("selected"));
  document.querySelector<HTMLElement>(`.admin-user-item[data-user-id="${CSS.escape(user.id)}"]`)?.classList.add("selected");
  const message = document.querySelector("#admin-message");
  const list = document.querySelector<HTMLDivElement>("#admin-map-list");
  if (message) message.textContent = "사용자의 맵 목록을 불러오는 중입니다.";
  if (list) list.replaceChildren();
  try {
    const maps = await adminClient.listMaps(user.id, authSession?.token);
    renderAdminMapList(user, maps);
    if (message) message.textContent = maps.length ? "맵을 선택하면 현재 편집 화면에서 열립니다." : "";
  } catch (error) {
    if (message) message.textContent = error instanceof Error ? error.message : "맵 목록을 불러오지 못했습니다.";
    if (message) message.classList.add("is-error");
  }
}

async function promoteAdminUser(user: AdminUser): Promise<void> {
  if (!adminClient) return;
  const message = document.querySelector("#admin-message");
  if (message) {
    message.classList.remove("is-error", "is-success");
    message.textContent = `${user.displayName}을(를) 관리자로 등록하는 중입니다.`;
  }
  try {
    await adminClient.promoteUser(user.id, authSession?.token);
    if (message) {
      message.classList.add("is-success");
      message.textContent = "관리자 등록이 완료됐습니다.";
    }
    await openAdminUsers(false);
  } catch (error) {
    if (message) {
      message.classList.add("is-error");
      message.textContent = error instanceof Error ? error.message : "관리자 등록에 실패했습니다.";
    }
  }
}

async function openAdminUsers(closeMenu = true): Promise<void> {
  if (closeMenu) setFileMenuOpen(false);
  const dialog = document.querySelector<HTMLDialogElement>("#admin-dialog");
  const message = document.querySelector<HTMLElement>("#admin-message");
  if (!dialog || !adminClient) return;
  adminSelectedUserId = null;
  if (message) {
    message.className = "dialog-message";
    message.textContent = "사용자 목록을 불러오는 중입니다.";
  }
  document.querySelector("#admin-maps-title")!.textContent = "저장된 맵";
  document.querySelector<HTMLDivElement>("#admin-map-list")!.innerHTML = "<span class=\"empty-library\">사용자를 선택하세요.</span>";
  if (!dialog.open) dialog.showModal();
  try {
    const users = await adminClient.listUsers(authSession?.token);
    renderAdminUserList(users);
    if (message) message.textContent = users.length ? "사용자를 선택하면 저장된 맵을 확인할 수 있습니다." : "";
  } catch (error) {
    if (message) {
      message.classList.add("is-error");
      message.textContent = error instanceof Error ? error.message : "사용자 목록을 불러오지 못했습니다.";
    }
  }
}

async function loadAdminMap(userId: string, mapId: string, button: HTMLButtonElement): Promise<void> {
  if (!adminClient) return;
  button.disabled = true;
  const message = document.querySelector("#admin-message");
  if (message) message.textContent = "맵을 불러오는 중입니다.";
  try {
    const saved = await adminClient.loadMap(userId, mapId, authSession?.token);
    const payload = typeof saved.payload === "string" ? saved.payload : JSON.stringify(saved.payload);
    const loaded = deserializeMap(payload);
    if (!loaded) throw new Error("저장된 맵 데이터가 올바르지 않습니다.");
    savedMapId = null;
    replaceMap(loaded);
    document.querySelector<HTMLDialogElement>("#admin-dialog")?.close();
  } catch (error) {
    if (message) {
      message.classList.add("is-error");
      message.textContent = error instanceof Error ? error.message : "맵을 불러오지 못했습니다.";
    }
  } finally {
    button.disabled = false;
  }
}

async function refreshAdminAccess(): Promise<void> {
  if (!adminClient) {
    setAdminMenuVisible(false);
    return;
  }
  try {
    await adminClient.listUsers(authSession?.token);
    setAdminMenuVisible(true);
  } catch {
    setAdminMenuVisible(false);
  }
}

function renderImageLibrary(items: readonly ImageAsset[]): void {
  const list = document.querySelector<HTMLDivElement>("#image-library-list")!;
  const count = document.querySelector<HTMLSpanElement>("#image-page-count");
  list.replaceChildren();
  if (count) count.textContent = items.length ? `${items.length}개` : "";
  if (items.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty-library";
    empty.textContent = "저장한 이미지가 없습니다.";
    list.append(empty);
    return;
  }
  const grid = document.createElement("div");
  grid.className = "image-library-grid";
  grid.setAttribute("role", "radiogroup");
  grid.setAttribute("aria-label", "저장한 이미지 선택");
  for (const asset of items) {
    const card = document.createElement("figure");
    card.className = "image-library-card";
    card.dataset.imageId = asset.id;
    card.setAttribute("role", "radio");
    card.setAttribute("aria-checked", "false");
    card.tabIndex = 0;
    const preview = document.createElement("button");
    preview.type = "button";
    preview.className = "image-library-preview";
    preview.tabIndex = -1;
    preview.setAttribute("aria-label", `${asset.originalFilename} 크게 보기`);
    const image = document.createElement("img");
    image.src = asset.thumbnailUrl;
    image.alt = asset.originalFilename;
    image.loading = "lazy";
    image.referrerPolicy = "no-referrer";
    preview.append(image);
    preview.addEventListener("click", (event) => {
      if (event.target !== image) return;
      event.stopPropagation();
      openImageViewer(asset);
    });
    const caption = document.createElement("figcaption");
    const filename = document.createElement("strong");
    filename.textContent = asset.originalFilename;
    const metadata = document.createElement("small");
    metadata.textContent = `${Math.ceil(asset.byteSize / 1024)} KB`;
    caption.append(filename, metadata);
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "button danger image-library-delete";
    remove.textContent = "삭제";
    remove.addEventListener("click", (event) => {
      event.stopPropagation();
      void deleteImageAsset(asset, remove);
    });
    caption.append(remove);
    card.append(preview, caption);
    const selectCard = (): void => {
      const group = card.closest<HTMLElement>('[role="radiogroup"]');
      group?.querySelectorAll<HTMLElement>('[role="radio"]').forEach((item) => {
        const selected = item === card;
        item.classList.toggle("is-selected", selected);
        item.setAttribute("aria-checked", String(selected));
      });
      card.focus();
    };
    card.addEventListener("click", (event) => {
      if (event.target === image) return;
      selectCard();
    });
    card.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      selectCard();
    });
    grid.append(card);
  }
  list.append(grid);
}
async function deleteImageAsset(asset: ImageAsset, button: HTMLButtonElement): Promise<void> {
  if (!authSession || !imageLibraryClient) return;
  if (!window.confirm(`"${asset.originalFilename}" 이미지를 DB 목록에서 삭제할까요? 원본 파일은 삭제하지 않습니다.`)) return;
  button.disabled = true;
  setDialogMessage("#image-library-message", "이미지를 삭제하는 중입니다.");
  try {
    await imageLibraryClient.deleteImage(authSession.token, asset.id);
    imageAssetsById.delete(asset.id);
    imageRenderImages.delete(asset.id);
    const result = await imageLibraryClient.listImages(authSession.token);
    renderImageLibrary(result.images);
    setDialogMessage("#image-library-message", "이미지 목록에서 삭제했습니다. 원본 파일은 유지됩니다.", "success");
  } catch (error) {
    setDialogMessage("#image-library-message", error instanceof Error ? error.message : "이미지를 삭제하지 못했습니다.", "error");
    showImageDebugDialog(error);
  } finally {
    button.disabled = false;
  }
}
function openImageViewer(asset: ImageAsset): void {
  const dialog = document.querySelector<HTMLDialogElement>("#image-viewer-dialog");
  const image = document.querySelector<HTMLImageElement>("#image-viewer-image");
  const external = document.querySelector<HTMLAnchorElement>("#image-viewer-external");
  if (!dialog || !image || !external) return;
  image.src = asset.originalUrl;
  image.alt = asset.originalFilename;
  image.referrerPolicy = "no-referrer";
  external.href = asset.originalUrl;
  external.setAttribute("aria-label", `${asset.originalFilename} 새 창에서 보기`);
  if (!dialog.open) dialog.showModal();
}
function setImagePaletteMessage(message: string, kind: "error" | "" = ""): void {
  const target = document.querySelector<HTMLParagraphElement>("#image-palette-message");
  if (!target) return;
  target.textContent = message;
  target.classList.toggle("is-error", kind === "error");
}
function cacheImageForMap(asset: ImageAsset): void {
  if (asset.id.startsWith("builtin:")) defaultImageAssetsById.set(asset.id, asset);
  else imageAssetsById.set(asset.id, asset);
  if (imageRenderImages.has(asset.id)) return;
  const image = new Image();
  image.crossOrigin = "anonymous";
  image.referrerPolicy = "no-referrer";
  image.src = asset.originalUrl;
  image.addEventListener("load", render);
  image.addEventListener("error", () => {
    const fallback = new Image();
    fallback.referrerPolicy = "no-referrer";
    fallback.src = asset.originalUrl;
    fallback.addEventListener("load", render);
    imageRenderImages.set(asset.id, fallback);
  }, { once: true });
  imageRenderImages.set(asset.id, image);
}
function findImageAsset(imageId: string | null | undefined): ImageAsset | undefined {
  if (!imageId) return undefined;
  return imageAssetsById.get(imageId) ?? defaultImageAssetsById.get(imageId);
}
function renderImageDetails(): void {
  const empty = document.querySelector<HTMLDivElement>("#image-detail-empty");
  const content = document.querySelector<HTMLElement>("#image-detail-content");
  const title = document.querySelector<HTMLHeadingElement>("#image-detail-title");
  const description = document.querySelector<HTMLParagraphElement>("#image-detail-description");
  const preview = document.querySelector<HTMLImageElement>("#selected-image-preview");
  const name = document.querySelector<HTMLSpanElement>("#selected-image-name");
  const hint = document.querySelector<HTMLParagraphElement>("#image-detail-hint");
  if (!empty || !content || !title || !description || !preview || !name || !hint) return;

  const placement = selectedImagePlacementIndex === null ? null : map.images[selectedImagePlacementIndex];
  const imageId = placement?.imageId ?? selectedImageId;
  const asset = findImageAsset(imageId);
  const hasContext = selectedLayer === "image" && Boolean(imageId);
  empty.classList.toggle("hidden", hasContext);
  content.classList.toggle("hidden", !hasContext);
  if (!hasContext) return;

  title.textContent = asset?.originalFilename ?? "배치 이미지";
  description.textContent = placement
    ? "선택한 이미지의 변환 값을 현재 문맥에서 조정합니다."
    : "다음 이미지 배치에 적용할 변환 값을 준비합니다.";
  if (asset) {
    preview.src = asset.thumbnailUrl;
    preview.alt = asset.originalFilename;
    preview.classList.remove("hidden");
    name.textContent = asset.originalFilename;
  } else {
    preview.removeAttribute("src");
    preview.alt = "";
    preview.classList.add("hidden");
    name.textContent = "저장된 이미지 정보가 아직 없습니다.";
  }
  hint.textContent = placement
    ? "값을 바꾸면 선택한 이미지에 즉시 적용됩니다."
    : "배치 전에 설정한 값은 다음 이미지에 적용됩니다.";
}
function syncImageTransformControls(): void {
  const rotation = document.querySelector<HTMLInputElement>("#image-rotation");
  const scale = document.querySelector<HTMLInputElement>("#image-scale");
  const rotationSlider = document.querySelector<HTMLInputElement>("#image-rotation-slider");
  const scaleSlider = document.querySelector<HTMLInputElement>("#image-scale-slider");
  const rotationValue = document.querySelector<HTMLOutputElement>("#image-rotation-value");
  const scaleValue = document.querySelector<HTMLOutputElement>("#image-scale-value");
  const summary = document.querySelector<HTMLSpanElement>("#image-transform-summary");
  if (!rotation || !scale || !rotationSlider || !scaleSlider || !rotationValue || !scaleValue || !summary) return;
  const rotationText = `${Math.round(selectedImageRotation)}°`;
  const scaleText = `${Math.round(selectedImageScale * 100)}%`;
  rotation.value = String(Math.round(selectedImageRotation));
  scale.value = String(Math.round(selectedImageScale * 100));
  rotationSlider.value = String(Math.round(selectedImageRotation));
  scaleSlider.value = String(Math.round(selectedImageScale * 100));
  rotationValue.value = rotationText;
  scaleValue.value = scaleText;
  summary.textContent = `${rotationText} · ${scaleText}`;
  const disabled = !selectedImageId && selectedImagePlacementIndex === null;
  rotation.disabled = disabled;
  scale.disabled = disabled;
  rotationSlider.disabled = disabled;
  scaleSlider.disabled = disabled;
  const reset = document.querySelector<HTMLButtonElement>("#reset-image-transform");
  if (reset) reset.disabled = disabled;
  document.querySelectorAll<HTMLButtonElement>(".transform-reset-button").forEach((button) => { button.disabled = disabled; });
  renderImageDetails();
}
function renderImageMaterialTabs(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-image-material-tab]").forEach((button) => {
    const active = button.dataset.imageMaterialTab === imageMaterialTab;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  });
}
function createImageMaterialOption(asset: ImageAsset): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "image-material-option";
  button.dataset.imageId = asset.id;
  button.title = asset.originalFilename;
  button.classList.toggle("selected", selectedImageId === asset.id);
  button.setAttribute("aria-pressed", String(selectedImageId === asset.id));
  const image = document.createElement("img");
  image.src = asset.thumbnailUrl;
  image.alt = asset.originalFilename;
  image.loading = "lazy";
  image.referrerPolicy = "no-referrer";
  const label = document.createElement("span");
  label.textContent = asset.originalFilename;
  button.append(image, label);
  button.addEventListener("click", () => {
    cacheImageForMap(asset);
    selectedImageId = asset.id;
    selectedImagePlacementIndex = null;
    setImageMode("place");
    renderImageMaterials();
    syncImageTransformControls();
  });
  return button;
}
function renderImageMaterials(): void {
  const grid = document.querySelector<HTMLDivElement>("#image-material-grid");
  const count = document.querySelector<HTMLSpanElement>("#image-material-count");
  if (!grid || !count) return;
  renderImageMaterialTabs();
  grid.replaceChildren();

  if (imageMaterialTab === "default") {
    count.textContent = `${defaultImageAssets.length}개`;
    setImagePaletteMessage("숲에 어울리는 투명 이미지를 기본으로 제공합니다.");
    for (const asset of defaultImageAssets) {
      cacheImageForMap(asset);
      grid.append(createImageMaterialOption(asset));
    }
    syncImageTransformControls();
    return;
  }

  count.textContent = imageAssets.length ? `${imageAssets.length}개` : "";
  if (!authSession || !imageLibraryClient) {
    setImagePaletteMessage("나의 이미지를 보려면 로그인이 필요합니다.", "error");
    syncImageTransformControls();
    return;
  }
  if (imageAssetsLoading) {
    setImagePaletteMessage("저장한 이미지를 불러오는 중입니다.");
    syncImageTransformControls();
    return;
  }
  if (imageAssets.length === 0) {
    setImagePaletteMessage(imageAssetsLoaded ? "저장한 이미지가 없습니다. 내 이미지에서 먼저 저장해 주세요." : "이미지 재료를 불러올 준비 중입니다.");
    syncImageTransformControls();
    return;
  }
  setImagePaletteMessage(selectedImageId ? "이미지를 고른 뒤 맵을 클릭하면 배치합니다." : "배치할 이미지를 선택해 주세요.");
  for (const asset of imageAssets) grid.append(createImageMaterialOption(asset));
  syncImageTransformControls();
}
async function refreshImageMaterials(): Promise<void> {
  if (!authSession || !imageLibraryClient || imageAssetsLoading || imageAssetsLoaded) {
    renderImageMaterials();
    return;
  }
  imageAssetsLoading = true;
  renderImageMaterials();
  try {
    const result = await imageLibraryClient.listImages(authSession.token);
    imageAssets = [...result.images];
    imageAssetsById.clear();
    for (const asset of imageAssets) cacheImageForMap(asset);
    imageAssetsLoaded = true;
    renderImageMaterials();
    render();
  } catch (error) {
    imageAssetsLoaded = false;
    setImagePaletteMessage(error instanceof Error ? error.message : "이미지 재료를 불러오지 못했습니다.", "error");
  } finally {
    imageAssetsLoading = false;
    renderImageMaterials();
  }
}
function renderStandaloneImageLibraryPage(): void {
  document.title = "내 이미지 · Forest Map Editor";
  document.querySelector<HTMLDivElement>("#app")!.innerHTML = `
    <div class="image-page-shell">
      <header class="image-page-header">
        <a class="brand" href="/" aria-label="Forest Map Editor 홈">
          <span class="brand-mark" aria-hidden="true">✦</span>
          <span><strong>FOREST</strong><small>IMAGE LIBRARY</small></span>
        </a>
        <a class="button ghost image-page-back" href="/">지도 편집기로 돌아가기</a>
      </header>
      <main class="image-page-main">
        <section class="image-page-card" aria-labelledby="image-page-title">
          <div class="image-page-heading">
            <div>
              <span class="eyebrow">MY IMAGES</span>
              <h1 id="image-page-title">내 이미지</h1>
              <p>로그인한 사용자만 이미지를 저장하고 목록을 볼 수 있습니다.</p>
            </div>
            <span class="image-page-count" id="image-page-count"></span>
          </div>
          <div class="image-upload-row image-page-upload">
            <input id="image-upload-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" />
            <button class="button primary" type="button" id="upload-image">이미지 저장</button>
          </div>
          <p class="image-upload-hint">1MB 초과 이미지는 전송 전에 브라우저에서 리샘플링 여부를 확인합니다. 취소하면 전송하지 않습니다.</p>
          <p class="dialog-message" id="image-library-message"></p>
          <div id="image-library-list" class="image-library-list image-page-list"></div>
        </section>
      </main>
      <footer class="image-page-footer"><a href="/">지도 편집기로 돌아가기</a><span>개인 이미지 보관함</span></footer>
    </div>
    <dialog id="image-debug-dialog" class="auth-debug-dialog" aria-labelledby="image-debug-title">
      <form method="dialog">
        <h2 id="image-debug-title">이미지 저장 개발자 진단</h2>
        <p id="image-debug-message">허용된 개발자 접속에서만 표시되는 상세 오류입니다.</p>
        <pre id="image-debug-details"></pre>
        <div class="dialog-actions"><button class="button primary">닫기</button></div>
      </form>
    </dialog>
    <dialog id="image-viewer-dialog" class="image-viewer-dialog" aria-label="이미지 크게 보기">
      <div class="image-viewer-panel">
        <button type="button" class="image-viewer-image-button" id="image-viewer-toggle" aria-label="이미지 크게 보기. 다시 누르면 닫힙니다." title="다시 누르면 닫힙니다.">
          <img id="image-viewer-image" alt="" />
        </button>
        <div class="image-viewer-actions">
          <a class="button primary" id="image-viewer-external" href="#" target="_blank" rel="noopener noreferrer">새창으로 보기</a>
        </div>
      </div>
    </dialog>`;
  document.querySelector<HTMLButtonElement>("#upload-image")!.addEventListener("click", () => { void uploadSelectedImage(); });
  const viewer = document.querySelector<HTMLDialogElement>("#image-viewer-dialog")!;
  document.querySelector<HTMLButtonElement>("#image-viewer-toggle")!.addEventListener("click", () => viewer.close());
  document.querySelector<HTMLAnchorElement>("#image-viewer-external")!.addEventListener("click", () => viewer.close());
  viewer.addEventListener("click", (event) => { if (event.target === viewer) viewer.close(); });
}

async function initializeStandaloneImageLibraryPage(): Promise<void> {
  await initializeAuth();
  document.querySelector<HTMLDialogElement>("#auth-dialog")?.remove();
  renderStandaloneImageLibraryPage();
  const input = document.querySelector<HTMLInputElement>("#image-upload-input")!;
  const button = document.querySelector<HTMLButtonElement>("#upload-image")!;
  if (!authSession || !imageLibraryClient) {
    input.disabled = true;
    button.disabled = true;
    setDialogMessage("#image-library-message", "이미지 저장과 목록 조회는 로그인이 필요합니다. 메인 페이지에서 로그인해 주세요.", "error");
    return;
  }
  setDialogMessage("#image-library-message", "저장한 이미지를 불러오는 중입니다.");
  try {
    const result = await imageLibraryClient.listImages(authSession.token);
    setDialogMessage("#image-library-message", result.images.length ? "저장한 이미지" : "저장한 이미지가 없습니다.");
    renderImageLibrary(result.images);
  } catch (error) {
    setDialogMessage("#image-library-message", error instanceof Error ? error.message : "이미지 목록을 불러오지 못했습니다.", "error");
    showImageDebugDialog(error);
  }
}
async function uploadSelectedImage(): Promise<void> {
  const input = document.querySelector<HTMLInputElement>("#image-upload-input")!;
  const button = document.querySelector<HTMLButtonElement>("#upload-image")!;
  if (!authSession || !imageLibraryClient) {
    setDialogMessage("#image-library-message", "이미지 저장은 로그인이 필요합니다.", "error");
    return;
  }
  const file = input.files?.[0];
  if (!file) {
    setDialogMessage("#image-library-message", "첨부할 이미지를 선택해 주세요.", "error");
    return;
  }
  const needsResample = isImageUploadOversized(file);
  if (needsResample) {
    const sizeInMegabytes = (file.size / (1024 * 1024)).toFixed(2);
    const shouldResample = window.confirm(
      `선택한 이미지가 ${sizeInMegabytes}MB입니다. 1MB 미만으로 줄이기 위해 브라우저에서 리샘플링할까요?\n\nPNG 투명 채널은 유지됩니다. 취소하면 업로드하지 않습니다.`,
    );
    if (!shouldResample) {
      setDialogMessage("#image-library-message", "리샘플링하지 않아 이미지 저장을 취소했습니다.");
      return;
    }
  }
  button.disabled = true;
  setDialogMessage(
    "#image-library-message",
    needsResample ? "브라우저에서 이미지 크기를 조정하는 중입니다." : "이미지를 저장하는 중입니다.",
  );
  try {
    const uploadFile = needsResample ? await prepareImageForUpload(file) : file;
    setDialogMessage("#image-library-message", "이미지를 저장하는 중입니다.");
    await imageLibraryClient.uploadImage(authSession.token, uploadFile, file.name);
    const result = await imageLibraryClient.listImages(authSession.token);
    renderImageLibrary(result.images);
    input.value = "";
    setDialogMessage("#image-library-message", "이미지를 저장했습니다.", "success");
  } catch (error) {
    setDialogMessage("#image-library-message", error instanceof Error ? error.message : "이미지를 저장하지 못했습니다.", "error");
    showImageDebugDialog(error);
  } finally {
    button.disabled = false;
  }
}
const RESIZE_ANCHORS = [
  "top-left", "top", "top-right", "left", "center", "right", "bottom-left", "bottom", "bottom-right",
] as const;
type ResizeAnchorId = (typeof RESIZE_ANCHORS)[number];

const resizePreviewGroundColors: Record<GroundType, string> = {
  grass: "#78a467", dirt: "#b18759", stone: "#96998d", water: "#5a9eaa",
};

function setResizeAnchor(anchor: ResizeAnchorId): void {
  const input = document.querySelector<HTMLInputElement>("#resize-anchor");
  if (!input) return;
  input.value = anchor;
  document.querySelectorAll<HTMLButtonElement>("[data-resize-anchor]").forEach((button) => {
    const active = button.dataset.resizeAnchor === anchor;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  });
  renderResizePreview();
}

function renderResizePreview(): void {
  const preview = document.querySelector<HTMLCanvasElement>("#resize-preview");
  const columnsInput = document.querySelector<HTMLInputElement>("#resize-columns");
  const rowsInput = document.querySelector<HTMLInputElement>("#resize-rows");
  const anchorInput = document.querySelector<HTMLInputElement>("#resize-anchor");
  if (!preview || !columnsInput || !rowsInput || !anchorInput) return;

  const width = preview.clientWidth || 640;
  const height = preview.clientHeight || 230;
  const devicePixelRatio = window.devicePixelRatio || 1;
  const pixelWidth = Math.max(1, Math.floor(width * devicePixelRatio));
  const pixelHeight = Math.max(1, Math.floor(height * devicePixelRatio));
  if (preview.width !== pixelWidth || preview.height !== pixelHeight) {
    preview.width = pixelWidth;
    preview.height = pixelHeight;
  }
  const previewContext = preview.getContext("2d");
  if (!previewContext) return;
  previewContext.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
  previewContext.clearRect(0, 0, width, height);
  previewContext.fillStyle = "#dfe2d8";
  previewContext.fillRect(0, 0, width, height);

  const columns = Number(columnsInput.value);
  const rows = Number(rowsInput.value);
  if (!Number.isInteger(columns) || !Number.isInteger(rows) || columns < MIN_MAP_SIZE || rows < MIN_MAP_SIZE || columns > MAX_MAP_SIZE || rows > MAX_MAP_SIZE) {
    previewContext.fillStyle = "#a24f43";
    previewContext.font = "600 12px Segoe UI, sans-serif";
    previewContext.fillText(`크기는 ${MIN_MAP_SIZE}~${MAX_MAP_SIZE} 사이의 정수여야 합니다.`, 16, height / 2);
    return;
  }

  const anchor = anchorInput.value as ResizeAnchor;
  const offsets = getResizeOffsets(map.columns, map.rows, columns, rows, anchor);
  const sourceX = offsets.column;
  const sourceY = offsets.row;
  const minX = Math.min(0, sourceX);
  const minY = Math.min(0, sourceY);
  const maxX = Math.max(columns, sourceX + map.columns);
  const maxY = Math.max(rows, sourceY + map.rows);
  const worldWidth = maxX - minX;
  const worldHeight = maxY - minY;
  const scale = Math.min((width - 28) / worldWidth, (height - 38) / worldHeight);
  const originX = (width - worldWidth * scale) / 2 - minX * scale;
  const originY = (height - worldHeight * scale) / 2 - minY * scale;
  const toX = (value: number) => originX + value * scale;
  const toY = (value: number) => originY + value * scale;
  const targetLeft = toX(0);
  const targetTop = toY(0);
  const targetWidth = columns * scale;
  const targetHeight = rows * scale;
  const sourceLeft = toX(sourceX);
  const sourceTop = toY(sourceY);
  const sourceWidth = map.columns * scale;
  const sourceHeight = map.rows * scale;

  previewContext.fillStyle = "rgba(248, 249, 242, .9)";
  previewContext.fillRect(targetLeft, targetTop, targetWidth, targetHeight);
  previewContext.globalAlpha = .82;
  for (let row = 0; row < map.rows; row += 1) {
    for (let column = 0; column < map.columns; column += 1) {
      const cell = map.cells[cellIndex(map, column, row)];
      previewContext.fillStyle = resizePreviewGroundColors[cell.ground];
      previewContext.fillRect(toX(sourceX + column), toY(sourceY + row), scale + .5, scale + .5);
      if (cell.prop) {
        previewContext.fillStyle = "rgba(35, 55, 42, .72)";
        previewContext.beginPath();
        previewContext.arc(toX(sourceX + column + .5), toY(sourceY + row + .5), Math.max(1.5, scale * .16), 0, Math.PI * 2);
        previewContext.fill();
      }
    }
  }
  previewContext.globalAlpha = 1;

  previewContext.save();
  previewContext.filter = "blur(2px)";
  previewContext.globalAlpha = .62;
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const outsideSource = column < sourceX || column >= sourceX + map.columns || row < sourceY || row >= sourceY + map.rows;
      if (!outsideSource) continue;
      previewContext.fillStyle = "#4f9e5b";
      previewContext.fillRect(toX(column), toY(row), scale + .5, scale + .5);
    }
  }
  for (let row = 0; row < map.rows; row += 1) {
    for (let column = 0; column < map.columns; column += 1) {
      const targetColumn = sourceX + column;
      const targetRow = sourceY + row;
      const outsideTarget = targetColumn < 0 || targetColumn >= columns || targetRow < 0 || targetRow >= rows;
      if (!outsideTarget) continue;
      previewContext.fillStyle = "#bc4e45";
      previewContext.fillRect(toX(targetColumn), toY(targetRow), scale + .5, scale + .5);
    }
  }
  previewContext.restore();

  previewContext.globalAlpha = 1;
  previewContext.setLineDash([5, 4]);
  previewContext.lineWidth = 1.5;
  previewContext.strokeStyle = "#3f8b4c";
  previewContext.strokeRect(targetLeft + .75, targetTop + .75, Math.max(0, targetWidth - 1.5), Math.max(0, targetHeight - 1.5));
  previewContext.strokeStyle = "rgba(167, 72, 63, .8)";
  previewContext.strokeRect(sourceLeft + .75, sourceTop + .75, Math.max(0, sourceWidth - 1.5), Math.max(0, sourceHeight - 1.5));
  previewContext.setLineDash([]);
  previewContext.fillStyle = "#31583a";
  previewContext.font = "600 10px Segoe UI, sans-serif";
  previewContext.fillText(`새 영역 ${columns} × ${rows}`, targetLeft + 7, targetTop + 14);
}

function openResizeMapDialog(): void {
  setFileMenuOpen(false);
  (document.querySelector<HTMLInputElement>("#resize-columns")!).value = String(map.columns);
  (document.querySelector<HTMLInputElement>("#resize-rows")!).value = String(map.rows);
  setResizeAnchor("center");
  setDialogMessage("#resize-map-message", "");
  const dialog = document.querySelector<HTMLDialogElement>("#resize-map-dialog")!;
  dialog.showModal();
  requestAnimationFrame(renderResizePreview);
}
function applyMapResize(): void {
  const columns = Number(document.querySelector<HTMLInputElement>("#resize-columns")!.value);
  const rows = Number(document.querySelector<HTMLInputElement>("#resize-rows")!.value);
  const anchor = document.querySelector<HTMLInputElement>("#resize-anchor")!.value as ResizeAnchor;
  try {
    replaceMap(resizeMap(map, columns, rows, anchor));
    document.querySelector<HTMLDialogElement>("#resize-map-dialog")!.close();
  } catch (error) {
    setDialogMessage("#resize-map-message", error instanceof Error ? error.message : "맵 크기를 적용하지 못했습니다.", "error");
  }
}
type WebkitDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type WebkitFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};
function getFullscreenElement(): Element | null {
  const safariDocument = document as WebkitDocument;
  return document.fullscreenElement ?? safariDocument.webkitFullscreenElement ?? null;
}
function updateFullscreenButton(): void {
  const nativeFullscreen = getFullscreenElement() !== null;
  const active = nativeFullscreen || fullscreenFallback;
  document.querySelectorAll<SVGElement>("[data-fullscreen-icon]").forEach((icon) => {
    icon.classList.toggle("hidden", icon.dataset.fullscreenIcon !== (active ? "contract" : "expand"));
  });
  const button = document.querySelector<HTMLButtonElement>("#toggle-fullscreen")!;
  const label = active ? "표준 모드로 돌아가기" : "전체 화면으로 전환";
  button.setAttribute("aria-pressed", String(active));
  button.setAttribute("aria-label", label);
  button.title = label;
  document.body.classList.toggle("fullscreen-fallback", active && !nativeFullscreen);
}
async function toggleFullscreen(): Promise<void> {
  const safariDocument = document as WebkitDocument;
  const current = getFullscreenElement();
  try {
    if (current) {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (safariDocument.webkitExitFullscreen) await safariDocument.webkitExitFullscreen();
      fullscreenFallback = false;
    } else {
      const root = document.documentElement as WebkitFullscreenElement;
      if (root.requestFullscreen) await root.requestFullscreen();
      else if (root.webkitRequestFullscreen) await root.webkitRequestFullscreen();
      else fullscreenFallback = true;
    }
  } catch (error) {
    console.warn("Fullscreen mode is unavailable", error);
    fullscreenFallback = true;
  }
  updateFullscreenButton();
}

function saveAuthSession(session: AuthSession | null): void {
  authSession = session;
  if (session) {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
    sessionStorage.removeItem(LEGACY_AUTH_STORAGE_KEY);
  } else {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_AUTH_STORAGE_KEY);
  }
}

function restoreAuthSession(): AuthSession | null {
  const raw = localStorage.getItem(AUTH_STORAGE_KEY) ?? sessionStorage.getItem(LEGACY_AUTH_STORAGE_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as Partial<AuthSession>;
    if (
      typeof session.token !== "string" ||
      typeof session.profile?.id !== "string" ||
      typeof session.profile.email !== "string" ||
      typeof session.profile.displayName !== "string" ||
      !isAvatarIcon(session.profile.avatarIcon)
    ) return null;
    return session as AuthSession;
  } catch {
    return null;
  }
}

function renderProfile(): void {
  const slot = document.querySelector<HTMLDivElement>("#account-menu-slot")!;
  const accountSection = document.querySelector<HTMLElement>("#account-menu-section");
  const loginTrigger = document.querySelector<HTMLButtonElement>("#login-trigger");
  slot.classList.remove("auth-ready");
  slot.classList.remove("auth-logged-out");
  slot.classList.add("auth-visible");
  accountSection?.classList.remove("hidden");
  loginTrigger?.classList.add("hidden");
  if (!authSession) return;
  const button = document.createElement("button");
  const avatar = document.createElement("span");
  const name = document.createElement("strong");
  button.type = "button";
  button.className = "profile-button";
  const shouldRename = authSession.profile.displayName === DEFAULT_DISPLAY_NAME;
  button.title = shouldRename ? "표시 이름을 수정하세요." : "계정 정보 수정";
  button.setAttribute("aria-label", shouldRename ? "표시 이름 수정하기" : "내 계정 정보 열기");
  avatar.textContent = avatarGlyph(authSession.profile.avatarIcon, authSession.profile.displayName);
  name.textContent = authSession.profile.displayName;
  if (authSession.profile.avatarIcon === "hidden") button.classList.add("icon-hidden");
  else button.append(avatar);
  button.append(name);
  button.addEventListener("click", openProfileDialog);
  slot.replaceChildren(button);
}

function renderLoggedOut(): void {
  document.querySelector<HTMLElement>("#account-menu-section")?.classList.remove("hidden");
  document.querySelector<HTMLDivElement>("#account-menu-slot")?.replaceChildren();
  document.querySelector<HTMLButtonElement>("#login-trigger")?.classList.remove("hidden");
}

function avatarGlyph(icon: AvatarIcon, displayName: string): string {
  if (icon === "initial") return displayName.trim().charAt(0).toUpperCase() || "?";
  return avatarOptions.find((option) => option.id === icon)?.glyph ?? "";
}

function selectAvatarIcon(icon: AvatarIcon): void {
  pendingAvatarIcon = icon;
  document.querySelectorAll<HTMLButtonElement>("[data-avatar-icon]").forEach((button) => {
    const selected = button.dataset.avatarIcon === icon;
    button.classList.toggle("selected", selected);
    button.setAttribute("aria-pressed", String(selected));
  });
  if (!authSession) return;
  const preview = document.querySelector<HTMLSpanElement>("#profile-avatar")!;
  preview.classList.toggle("is-hidden", icon === "hidden");
  preview.textContent = avatarGlyph(icon, authSession.profile.displayName);
}

function openProfileDialog(): void {
  if (!authSession) return;
  document.querySelector("#profile-email")!.textContent = authSession.profile.email;
  (document.querySelector("#profile-name") as HTMLInputElement).value = authSession.profile.displayName;
  document.querySelector("#profile-message")!.textContent = "변경한 이름과 아이콘은 계정 설정에 저장됩니다.";
  selectAvatarIcon(authSession.profile.avatarIcon);
  document.querySelector<HTMLDialogElement>("#profile-dialog")!.showModal();
}

function renderAuthNote(message: string, title?: string): void {
  const note = document.createElement("span");
  note.className = "auth-note";
  note.textContent = message;
  if (title) note.title = title;
  const slot = document.querySelector<HTMLDivElement>("#auth-slot")!;
  slot.classList.remove("auth-ready");
  slot.classList.remove("auth-logged-out");
  slot.classList.add("auth-visible");
  slot.replaceChildren(note);
}

function showAuthDebugDialog(error: unknown): void {
  if (!(error instanceof AuthApiError) || !error.debug) return;
  const dialog = document.querySelector<HTMLDialogElement>("#auth-debug-dialog");
  const message = document.querySelector("#auth-debug-message");
  const details = document.querySelector("#auth-debug-details");
  if (!dialog || !message || !details) return;
  message.textContent = `${error.status} ${error.code}: ${error.message}`;
  details.textContent = JSON.stringify(error.debug, null, 2);
  if (!dialog.open) dialog.showModal();
}

function showImageDebugDialog(error: unknown): void {
  if (!(error instanceof ImageLibraryError) || !error.debug) return;
  const dialog = document.querySelector<HTMLDialogElement>("#image-debug-dialog");
  const message = document.querySelector("#image-debug-message");
  const details = document.querySelector("#image-debug-details");
  if (!dialog || !message || !details) return;
  message.textContent = `${error.status ?? "?"} ${error.code ?? "REQUEST_FAILED"}: ${error.message}`;
  details.textContent = JSON.stringify(error.debug, null, 2);
  if (!dialog.open) dialog.showModal();
}

function showMapDebugDialog(error: unknown): void {
  if (!(error instanceof MapStorageError) || !error.debug) return;
  const dialog = document.querySelector<HTMLDialogElement>("#map-debug-dialog");
  const message = document.querySelector("#map-debug-message");
  const details = document.querySelector("#map-debug-details");
  if (!dialog || !message || !details) return;
  message.textContent = `${error.status ?? "?"} ${error.code ?? "REQUEST_FAILED"}: ${error.message}`;
  details.textContent = JSON.stringify(error.debug, null, 2);
  if (!dialog.open) dialog.showModal();
}

function renderAuthRetry(label: string, retry: () => void, error?: unknown): void {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "auth-retry";
  button.textContent = label;
  button.addEventListener("click", retry);
  const slot = document.querySelector<HTMLDivElement>("#auth-slot")!;
  slot.classList.remove("auth-ready");
  slot.classList.remove("auth-logged-out");
  slot.classList.add("auth-visible");
  slot.replaceChildren(button);
  showAuthDebugDialog(error);
}

function openAuthDialog(): void {
  const dialog = document.querySelector<HTMLDialogElement>("#auth-dialog");
  if (!dialog) return;
  if (!dialog.open) dialog.showModal();
  void renderGoogleSignIn();
}

function randomAuthValue(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/u, "");
}

function googleRedirectUri(): string {
  return new URL(window.location.pathname, window.location.origin).href;
}

function clearGoogleRedirectState(): void {
  sessionStorage.removeItem("mapeditor-google-state");
  sessionStorage.removeItem("mapeditor-google-nonce");
  window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
}

async function startGoogleLoginRedirect(button: HTMLButtonElement): Promise<void> {
  if (!googleClientId) return;
  button.disabled = true;
  const state = randomAuthValue();
  const nonce = randomAuthValue();
  sessionStorage.setItem("mapeditor-google-state", state);
  sessionStorage.setItem("mapeditor-google-nonce", nonce);
  const params = new URLSearchParams({
    client_id: googleClientId,
    redirect_uri: googleRedirectUri(),
    response_type: "id_token",
    response_mode: "fragment",
    scope: "openid email profile",
    state,
    nonce,
    prompt: "select_account",
  });
  window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
}

async function handleGoogleLoginRedirect(): Promise<boolean> {
  const params = new URLSearchParams(window.location.hash.replace(/^#/u, ""));
  const credential = params.get("id_token");
  const error = params.get("error");
  if (!credential && !error) return false;

  const expectedState = sessionStorage.getItem("mapeditor-google-state");
  const expectedNonce = sessionStorage.getItem("mapeditor-google-nonce");
  clearGoogleRedirectState();
  if (error) {
    const dialog = document.querySelector<HTMLDialogElement>("#auth-dialog");
    if (dialog && !dialog.open) dialog.showModal();
    renderAuthRetry("Google 로그인 다시 시도", () => { void renderGoogleSignIn(); });
    return true;
  }
  if (!credential || !expectedState || params.get("state") !== expectedState || !expectedNonce) {
    const dialog = document.querySelector<HTMLDialogElement>("#auth-dialog");
    if (dialog && !dialog.open) dialog.showModal();
    renderAuthRetry("Google 로그인 다시 시도", () => { void renderGoogleSignIn(); });
    return true;
  }
  if (!authClient) return true;
  renderAuthNote("로그인 확인 중…");
  try {
    saveAuthSession(await authClient.login(credential, expectedNonce));
    renderProfile();
    void refreshAdminAccess();
    imageMaterialTab = "mine";
    renderImageMaterials();
    void refreshImageMaterials();
  } catch (loginError) {
    console.error("Google redirect login failed", loginError);
    const dialog = document.querySelector<HTMLDialogElement>("#auth-dialog");
    if (dialog && !dialog.open) dialog.showModal();
    renderAuthRetry("Google 로그인 다시 시도", () => { void renderGoogleSignIn(); }, loginError);
  }
  return true;
}

async function loadGoogleIdentity(): Promise<void> {
  if (window.google) return;
  if (googleIdentityLoadPromise) return googleIdentityLoadPromise;
  googleIdentityLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[src="https://accounts.google.com/gsi/client"]');
    const script = existing ?? document.createElement("script");
    const onLoad = (): void => window.google
      ? resolve()
      : reject(new Error("Google Identity API가 준비되지 않았습니다."));
    const onError = (): void => reject(new Error("Google 로그인 라이브러리를 불러오지 못했습니다."));
    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    if (!existing) {
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      document.head.append(script);
    }
  });
  try {
    await googleIdentityLoadPromise;
  } catch (error) {
    googleIdentityLoadPromise = null;
    throw error;
  }
}

async function handleGoogleCredential(response: GoogleCredentialResponse): Promise<void> {
  if (!authClient) return;
  renderAuthNote("로그인 확인 중…");
  try {
    saveAuthSession(await authClient.login(response.credential));
    renderProfile();
    void refreshAdminAccess();
    imageMaterialTab = "mine";
    renderImageMaterials();
    void refreshImageMaterials();
    document.querySelector<HTMLDialogElement>("#auth-dialog")?.close();
  } catch (error) {
    console.error("Google login failed", error);
    renderAuthRetry("로그인 실패 · 다시 시도", () => { void renderGoogleSignIn(); }, error);
  }
}

async function renderGoogleSignIn(): Promise<void> {
  const slot = document.querySelector<HTMLDivElement>("#auth-slot")!;
  slot.replaceChildren();
  slot.classList.remove("auth-ready", "auth-logged-out");
  slot.classList.add("auth-visible");
  if (!googleClientId) {
    renderAuthNote("로그인 설정 필요", "app-config.json에 Google OAuth 클라이언트 ID를 설정해야 합니다.");
    return;
  }
  const button = document.createElement("button");
  button.type = "button";
  button.className = "google-login-button";
  button.textContent = "Google 로그인";
  button.addEventListener("click", () => { void startGoogleLoginRedirect(button); });
  slot.replaceChildren(button);
  return;
  slot.classList.remove("auth-visible");
  if (!googleClientId) {
    renderAuthNote("로그인 설정 필요", "app-config.json에 Google OAuth 클라이언트 ID를 설정해야 합니다.");
    return;
  }
  renderAuthNote("Google 로그인 로딩…");
  slot.classList.remove("auth-visible");
  try {
    await loadGoogleIdentity();
    if (!window.google) throw new Error("Google Identity API가 준비되지 않았습니다.");
    slot.replaceChildren();
    window.google!.accounts.id.initialize({
      client_id: googleClientId,
      callback: (response) => { void handleGoogleCredential(response); },
      ux_mode: "popup",
    });
    window.google!.accounts.id.renderButton(slot, {
      theme: "outline", size: "medium", shape: "rectangular", text: "signin_with", locale: "ko", width: 200,
    });
    slot.classList.add("auth-ready");
    slot.classList.add("auth-visible");
  } catch (error) {
    console.error("Google Identity initialization failed", error);
    renderAuthRetry("로그인 다시 시도", () => { void renderGoogleSignIn(); });
  }
}

async function initializeAuth(): Promise<void> {
  renderLoggedOut();
  renderAuthNote("로그인 준비 중");
  try {
    const configResponse = await fetch("/app-config.json", { cache: "no-store" });
    if (!configResponse.ok) throw new Error("앱 설정 파일을 불러오지 못했습니다.");
    const config = parsePublicAppConfig(await configResponse.json());
    if (!config.apiBaseUrl || !config.googleClientId) {
      renderAuthNote("로그인 설정 필요", "app-config.json에 API 주소와 Google OAuth 클라이언트 ID를 설정해야 합니다.");
      return;
    }
    authClient = new AuthClient(config.apiBaseUrl);
    adminClient = new AdminClient(config.apiBaseUrl);
    imageLibraryClient = new ImageLibraryClient(config.apiBaseUrl);
    mapStorageClient = new MapStorageClient(config.apiBaseUrl);
    googleClientId = config.googleClientId;
    void initializeDeveloperAccess(config.apiBaseUrl);
    if (await handleGoogleLoginRedirect()) return;
    const restored = restoreAuthSession();
    if (restored) {
      try {
        const verified = await authClient.me(restored.token);
        saveAuthSession({ token: restored.token, profile: verified.profile });
        renderProfile();
        void refreshAdminAccess();
        imageMaterialTab = "mine";
        renderImageMaterials();
        void refreshImageMaterials();
        return;
      } catch {
        saveAuthSession(null);
      }
    }
    renderLoggedOut();
    void refreshAdminAccess();
  } catch (error) {
    console.error("Authentication setup failed", error);
    renderAuthRetry("로그인 서버 다시 연결", () => { void initializeAuth(); });
  }
}

async function initializeDeploymentTime(): Promise<void> {
  const target = document.querySelector<HTMLSpanElement>("#deployment-time");
  if (!target) return;
  try {
    const response = await fetch("/deployment-meta.json", { cache: "no-store" });
    if (!response.ok) throw new Error("deployment metadata unavailable");
    const metadata = parseDeploymentMetadata(await response.json());
    const fullTime = formatDeploymentTime(metadata.deployedAt);
    target.title = fullTime;
    target.setAttribute("aria-label", fullTime);
    const updateRelativeTime = () => {
      target.textContent = formatDeploymentRelativeTime(metadata.deployedAt);
    };
    updateRelativeTime();
    if (deploymentTimeTimer !== undefined) window.clearInterval(deploymentTimeTimer);
    deploymentTimeTimer = window.setInterval(updateRelativeTime, 60_000);
  } catch {
    target.textContent = "배포 시각 확인 불가";
    target.removeAttribute("title");
    target.removeAttribute("aria-label");
  }
}

async function initializeDeveloperAccess(apiBaseUrl: string): Promise<void> {
  const target = document.querySelector<HTMLButtonElement>("#developer-access");
  if (!target) return;
  developerAccess = false;
  target.disabled = true;
  target.onclick = () => {
    if (!target.disabled) window.location.assign("/diag/");
  };
  try {
    const response = await fetch(`${apiBaseUrl}/health`, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("health check failed");
    const body = await response.json() as { developerDebug?: unknown; d1UsagePercent?: unknown };
    const d1Usage = document.querySelector<HTMLSpanElement>("#d1-usage");
    if (d1Usage) {
      d1Usage.textContent = typeof body.d1UsagePercent === "number" && Number.isFinite(body.d1UsagePercent)
        ? `${Math.max(0, Math.min(100, Math.round(body.d1UsagePercent)))}%`
        : "--";
    }
    target.classList.remove("is-on", "is-off", "is-old", "is-unavailable", "is-link");
    if (typeof body.developerDebug !== "boolean") {
      target.textContent = "Developer: old API";
      target.classList.add("is-old");
      target.title = "The deployed Worker does not expose developer access status.";
      void refreshAdminAccess();
      return;
    }
    developerAccess = body.developerDebug;
    target.textContent = body.developerDebug ? "Developer: Enabled" : "Developer: Disabled";
    target.classList.add(body.developerDebug ? "is-on" : "is-off");
    target.disabled = !body.developerDebug;
    if (body.developerDebug) target.classList.add("is-link");
    target.title = body.developerDebug
      ? "This browser IP is allowlisted for developer diagnostics."
      : "This browser IP is not allowlisted for developer diagnostics.";
    void refreshAdminAccess();
  } catch {
    developerAccess = false;
    target.textContent = "Developer: unavailable";
    target.classList.remove("is-on", "is-off", "is-old");
    target.classList.add("is-unavailable");
    target.classList.remove("is-link");
    target.disabled = true;
    target.title = "The Worker health check could not be completed.";
    void refreshAdminAccess();
  }
}

const propModeCopy: Record<PropMode, { label: string; hint: string }> = {
  place: { label: "배치", hint: "사물을 누른 채 움직여 연속으로 배치합니다." },
  move: { label: "이동", hint: "사물을 드래그해 다른 셀로 옮깁니다. 도착한 사물은 덮어씁니다." },
  erase: { label: "지우개", hint: "셀을 누르거나 드래그해 사물만 지웁니다." },
};
function setPropMode(mode: PropMode): void {
  propMode = mode;
  document.querySelectorAll<HTMLButtonElement>("[data-prop-mode]").forEach((button) => {
    const active = button.dataset.propMode === mode;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  });
  document.querySelector("#prop-mode-label")!.textContent = propModeCopy[mode].label;
  document.querySelector("#prop-mode-hint")!.textContent = propModeCopy[mode].hint;
  canvas.classList.remove("prop-mode-place", "prop-mode-move", "prop-mode-erase");
  canvas.classList.add(`prop-mode-${mode}`);
}
function setGroundEditTab(tab: GroundEditTab): void {
  selectedGroundEditTab = tab;
  document.querySelectorAll<HTMLButtonElement>("[data-ground-edit-tab]").forEach((button) => {
    const active = button.dataset.groundEditTab === tab;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  });
  document.querySelector("#terrain-palette")?.classList.toggle("hidden", tab !== "terrain");
  document.querySelector("#elevation-palette")?.classList.toggle("hidden", tab !== "elevation");
  render();
}
function setGroundBrightness(value: number): void {
  if (!Number.isInteger(value) || value < 0 || value > 3) return;
  selectedGroundBrightness = value as BrightnessCorrection;
  document.querySelectorAll<HTMLButtonElement>("[data-ground-brightness]").forEach((button) => {
    const active = Number(button.dataset.groundBrightness) === selectedGroundBrightness;
    button.classList.toggle("selected", active);
    button.setAttribute("aria-checked", String(active));
  });
  render();
}
function setTileElevationOption(elevation: TileElevation): void {
  selectedTileElevation = elevation;
  document.querySelectorAll<HTMLButtonElement>("[data-tile-elevation]").forEach((button) => {
    const active = Number(button.dataset.tileElevation) === elevation;
    button.classList.toggle("selected", active);
    button.setAttribute("aria-checked", String(active));
  });
  render();
}
const imageModeCopy: Record<PropMode, { label: string; hint: string }> = {
  place: { label: "배치", hint: "이미지를 고른 뒤 맵을 클릭하면 배치합니다." },
  move: { label: "이동", hint: "맵의 이미지를 클릭한 뒤 다른 칸으로 끌어 이동합니다." },
  erase: { label: "지우기", hint: "맵의 이미지를 클릭하면 삭제합니다." },
};
function setImageMode(mode: PropMode): void {
  imageMode = mode;
  movingImage = null;
  document.querySelectorAll<HTMLButtonElement>("[data-image-mode]").forEach((button) => {
    const active = button.dataset.imageMode === mode;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  });
  const label = document.querySelector("#image-mode-label");
  const hint = document.querySelector("#image-mode-hint");
  const continuousToggle = document.querySelector<HTMLLabelElement>("#image-continuous-toggle");
  if (label) label.textContent = imageModeCopy[mode].label;
  if (hint) hint.textContent = imageModeCopy[mode].hint;
  continuousToggle?.classList.toggle("hidden", mode !== "place");
  continuousToggle?.setAttribute("aria-hidden", String(mode !== "place"));
  canvas.classList.remove("image-mode-place", "image-mode-move", "image-mode-erase");
  canvas.classList.add(`image-mode-${mode}`);
  render();
}
function applySelectedImageTransform(rotation: number, scale: number): void {
  selectedImageRotation = ((rotation % 360) + 360) % 360;
  selectedImageScale = Math.max(IMAGE_MIN_SCALE, Math.min(IMAGE_MAX_SCALE, scale));
  if (selectedImagePlacementIndex !== null) {
    const candidate = cloneMap(map);
    if (updateImageTransform(candidate, selectedImagePlacementIndex, selectedImageRotation, selectedImageScale)) {
      if (!imageTransformChanged) {
        history.push(cloneMap(map));
        if (history.length > 60) history.shift();
        future = [];
      }
      imageTransformChanged = true;
      map = candidate;
      render();
    }
  }
  syncImageTransformControls();
}

canvasScroll.addEventListener("pointerdown", (event) => {
  if (!trackTouchStart(event)) return;
  event.preventDefault();
  event.stopPropagation();
}, true);
canvasScroll.addEventListener("pointermove", (event) => {
  if (!trackTouchMove(event)) return;
  event.preventDefault();
  if (isPinching) updatePinch();
  event.stopPropagation();
}, true);
canvasScroll.addEventListener("pointerup", (event) => {
  if (!trackTouchEnd(event)) return;
  event.preventDefault();
  event.stopPropagation();
  if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
}, true);
canvasScroll.addEventListener("pointercancel", (event) => {
  if (!trackTouchEnd(event)) return;
  event.preventDefault();
  event.stopPropagation();
  if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
}, true);

canvas.addEventListener("pointerdown", (event) => {
  if (event.pointerType === "touch" && (isPinching || touchGestureLocked)) {
    event.preventDefault();
    return;
  }
  if (event.button === 1 || (event.button === 0 && (panMode || spacePressed))) {
    event.preventDefault();
    isDrawing = false;
    beginPan(event);
    return;
  }
  if (selectedLayer === "ground" && brushMode === "shape") {
    if (event.button !== 0 && event.button !== 2) return;
    const cell = getCell(event);
    if (!cell) return;
    event.preventDefault();
    isDrawing = true;
    strokeChanged = false;
    lastPaintedCell = `${cell.column}:${cell.row}`;
    shapeDrag = { start: cell, current: cell, erase: event.button === 2 };
    canvas.setPointerCapture(event.pointerId);
    render();
    return;
  }
  if (selectedLayer === "prop" && propMode === "move") {
    if (event.button !== 0) return;
    const cell = getCell(event);
    const prop = cell ? map.cells[cellIndex(map, cell.column, cell.row)].prop : null;
    if (!cell || !prop) return;
    event.preventDefault();
    isDrawing = true;
    strokeChanged = false;
    lastPaintedCell = `${cell.column}:${cell.row}`;
    movingProp = { prop, fromColumn: cell.column, fromRow: cell.row, target: cell };
    canvas.setPointerCapture(event.pointerId);
    render();
    return;
  }
  if (selectedLayer === "image" && imageMode === "move") {
    if (event.button !== 0) return;
    const cell = getCell(event);
    const index = cell ? getTopImageIndex(cell.column, cell.row) : -1;
    if (!cell || index < 0) return;
    event.preventDefault();
    selectImagePlacement(index);
    isDrawing = true;
    strokeChanged = false;
    lastPaintedCell = `${cell.column}:${cell.row}`;
    movingImage = { index, fromColumn: cell.column, fromRow: cell.row, target: cell };
    canvas.setPointerCapture(event.pointerId);
    render();
    return;
  }
  if (event.button !== 0 && event.button !== 2) return;
  event.preventDefault(); isDrawing = true; canvas.setPointerCapture(event.pointerId); paintAt(event);
});
canvas.addEventListener("pointermove", (event) => {
  if (event.pointerType === "touch" && (isPinching || touchGestureLocked)) return;
  if (isPanning) panAt(event);
  else paintAt(event);
});
canvas.addEventListener("pointerup", (event) => {
  if (event.pointerType === "touch" && (isPinching || touchGestureLocked)) return;
  finishStroke();
  if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
});
canvas.addEventListener("pointercancel", (event) => {
  if (event.pointerType === "touch" && (isPinching || touchGestureLocked)) return;
  movingProp = null;
  movingImage = null;
  shapeDrag = null;
  finishStroke();
  render();
  if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
});
canvas.addEventListener("contextmenu", (event) => event.preventDefault());
canvas.addEventListener("pointerleave", () => { document.querySelector("#cursor-status")!.textContent = "셀 위에 커서를 올려보세요"; });
canvasScroll.addEventListener("wheel", (event) => {
  if (event.deltaY === 0) return;
  event.preventDefault();
  const nextZoom = zoom * Math.pow(WHEEL_ZOOM_FACTOR, -event.deltaY / 100);
  setZoom(nextZoom, { x: event.clientX, y: event.clientY });
}, { passive: false });

document.querySelectorAll<HTMLButtonElement>("[data-layer]").forEach((button) => button.addEventListener("click", () => {
  selectedLayer = button.dataset.layer as Layer;
  if (selectedLayer === "prop") setPropMode("place");
  if (selectedLayer === "image") {
    setImageMode("place");
    void refreshImageMaterials();
  }
  document.querySelectorAll("[data-layer]").forEach((item) => item.classList.toggle("active", item === button));
  document.querySelector("#ground-palette")!.classList.toggle("hidden", selectedLayer !== "ground");
  document.querySelector("#prop-palette")!.classList.toggle("hidden", selectedLayer !== "prop");
  document.querySelector("#image-palette")!.classList.toggle("hidden", selectedLayer !== "image");
  renderImageDetails();
  render();
}));
document.querySelectorAll<HTMLButtonElement>("[data-ground-edit-tab]").forEach((button) => button.addEventListener("click", () => {
  setGroundEditTab(button.dataset.groundEditTab as GroundEditTab);
}));
document.querySelectorAll<HTMLButtonElement>("[data-tile-elevation]").forEach((button) => button.addEventListener("click", () => {
  setTileElevationOption(Number(button.dataset.tileElevation) as TileElevation);
}));
document.querySelectorAll<HTMLButtonElement>("[data-ground]").forEach((button) => button.addEventListener("click", () => {
  selectedGround = button.dataset.ground as GroundType;
  document.querySelectorAll("[data-ground]").forEach((item) => item.classList.toggle("selected", item === button));
}));
document.querySelectorAll<HTMLButtonElement>("[data-ground-brightness]").forEach((button) => button.addEventListener("click", () => {
  setGroundBrightness(Number(button.dataset.groundBrightness));
}));
document.querySelector<HTMLInputElement>("#brush-size-range")!.addEventListener("input", (event) => {
  setBrushSize(Number((event.target as HTMLInputElement).value));
});
document.querySelector<HTMLInputElement>("#brush-size-number")!.addEventListener("input", (event) => {
  setBrushSize(Number((event.target as HTMLInputElement).value));
});
document.querySelectorAll<HTMLButtonElement>("[data-brush-mode]").forEach((button) => button.addEventListener("click", () => {
  setBrushMode(button.dataset.brushMode as BrushMode);
}));
document.querySelectorAll<HTMLButtonElement>("[data-brush-shape]").forEach((button) => button.addEventListener("click", () => {
  setBrushShape(button.dataset.brushShape as BrushShape);
}));
document.querySelectorAll<HTMLButtonElement>("[data-prop]").forEach((button) => button.addEventListener("click", () => {
  selectedProp = button.dataset.prop as PropType;
  setPropMode("place");
  document.querySelectorAll("[data-prop]").forEach((item) => item.classList.toggle("selected", item === button));
}));
document.querySelectorAll<HTMLButtonElement>("[data-prop-mode]").forEach((button) => button.addEventListener("click", () => {
  setPropMode(button.dataset.propMode as PropMode);
}));
document.querySelectorAll<HTMLButtonElement>("[data-image-mode]").forEach((button) => button.addEventListener("click", () => {
  setImageMode(button.dataset.imageMode as PropMode);
}));
document.querySelector<HTMLInputElement>("#image-continuous-placement")!.addEventListener("change", (event) => {
  imageContinuousPlacement = (event.target as HTMLInputElement).checked;
});
document.querySelectorAll<HTMLButtonElement>("[data-image-material-tab]").forEach((button) => button.addEventListener("click", () => {
  imageMaterialTab = button.dataset.imageMaterialTab as ImageMaterialTab;
  renderImageMaterials();
  if (imageMaterialTab === "mine" && authSession) void refreshImageMaterials();
}));
document.querySelector<HTMLInputElement>("#image-rotation")!.addEventListener("input", (event) => {
  applySelectedImageTransform(Number((event.target as HTMLInputElement).value), selectedImageScale);
});
document.querySelector<HTMLInputElement>("#image-rotation-slider")!.addEventListener("input", (event) => {
  applySelectedImageTransform(Number((event.target as HTMLInputElement).value), selectedImageScale);
});
document.querySelectorAll<HTMLInputElement>("#image-rotation, #image-rotation-slider").forEach((input) => input.addEventListener("change", () => {
  imageTransformChanged = false;
  scheduleSave();
}));
document.querySelector<HTMLInputElement>("#image-scale")!.addEventListener("input", (event) => {
  applySelectedImageTransform(selectedImageRotation, Number((event.target as HTMLInputElement).value) / 100);
});
document.querySelector<HTMLInputElement>("#image-scale-slider")!.addEventListener("input", (event) => {
  applySelectedImageTransform(selectedImageRotation, Number((event.target as HTMLInputElement).value) / 100);
});
document.querySelectorAll<HTMLInputElement>("#image-scale, #image-scale-slider").forEach((input) => input.addEventListener("change", () => {
  imageTransformChanged = false;
  scheduleSave();
}));
document.querySelector("#reset-image-rotation")!.addEventListener("click", () => {
  applySelectedImageTransform(DEFAULT_IMAGE_ROTATION, selectedImageScale);
  imageTransformChanged = false;
  scheduleSave();
});
document.querySelector("#reset-image-scale")!.addEventListener("click", () => {
  applySelectedImageTransform(selectedImageRotation, DEFAULT_IMAGE_SCALE);
  imageTransformChanged = false;
  scheduleSave();
});
document.querySelector("#reset-image-transform")!.addEventListener("click", () => {
  applySelectedImageTransform(DEFAULT_IMAGE_ROTATION, DEFAULT_IMAGE_SCALE);
  imageTransformChanged = false;
  scheduleSave();
});
document.querySelector("#map-name")!.addEventListener("input", (event) => {
  map.name = (event.target as HTMLInputElement).value || "이름 없는 지도"; map.updatedAt = new Date().toISOString(); scheduleSave();
});
document.querySelector("#undo")!.addEventListener("click", undo);
document.querySelector("#redo")!.addEventListener("click", redo);
document.querySelector("#toggle-grid")!.addEventListener("click", (event) => {
  gridVisible = !gridVisible; const button = event.currentTarget as HTMLButtonElement;
  button.classList.toggle("active", gridVisible); button.setAttribute("aria-pressed", String(gridVisible)); render();
});
document.querySelector("#zoom-out")!.addEventListener("click", () => setZoom(zoom - ZOOM_STEP));
document.querySelector("#zoom-in")!.addEventListener("click", () => setZoom(zoom + ZOOM_STEP));
document.querySelector("#zoom-reset")!.addEventListener("click", resetViewport);
document.querySelector("#toggle-pan")!.addEventListener("click", () => {
  panMode = !panMode;
  updateViewport();
});
document.querySelector("#reset-map")!.addEventListener("click", () => { savedMapId = null; replaceMap(createInitialMap()); });
document.querySelector("#clear-map")!.addEventListener("click", () => {
  const next = createInitialMap(); next.name = "새로운 숲"; next.cells = next.cells.map(() => ({ ground: "grass", elevation: 0, brightnessCorrection: 0, prop: null })); savedMapId = null; replaceMap(next);
});
document.querySelector("#export-json")!.addEventListener("click", () => {
  setFileMenuOpen(false);
  download(new Blob([serializeMap(map)], { type: "application/json" }), "forest-map.json");
});
document.querySelector("#export-png")!.addEventListener("click", () => {
  setFileMenuOpen(false);
  const wasVisible = gridVisible; gridVisible = false; render();
  canvas.toBlob((blob) => { if (blob) download(blob, "forest-map.png"); gridVisible = wasVisible; render(); }, "image/png");
});
document.querySelector("#save-map")!.addEventListener("click", () => {
  if (savedMapId) {
    setFileMenuOpen(false);
    void saveMapToCloud(false);
  } else {
    openMapSaveDialog(false);
  }
});
document.querySelector("#save-map-as")!.addEventListener("click", () => openMapSaveDialog(true));
document.querySelector("#open-map-library")!.addEventListener("click", () => { void openMapLibrary(); });
document.querySelector("#open-image-library")!.addEventListener("click", () => { window.location.assign("/images/"); });
document.querySelector("#open-resize-map")!.addEventListener("click", openResizeMapDialog);
document.querySelector("#open-admin-users")!.addEventListener("click", () => { void openAdminUsers(); });
document.querySelector("#confirm-map-save")!.addEventListener("click", () => { void saveMapToCloud(); });
document.querySelector("#confirm-resize-map")!.addEventListener("click", applyMapResize);
document.querySelectorAll<HTMLButtonElement>("[data-resize-anchor]").forEach((button) => button.addEventListener("click", () => {
  setResizeAnchor(button.dataset.resizeAnchor as ResizeAnchorId);
}));
document.querySelectorAll<HTMLInputElement>("#resize-columns, #resize-rows").forEach((input) => input.addEventListener("input", renderResizePreview));
document.querySelector("#file-menu-toggle")!.addEventListener("click", (event) => {
  event.stopPropagation();
  setFileMenuOpen(!fileMenuOpen);
});
document.querySelector("#login-trigger")!.addEventListener("click", () => {
  setFileMenuOpen(false);
  window.location.assign("/login/");
});
document.querySelector("#toggle-fullscreen")!.addEventListener("click", () => { void toggleFullscreen(); });
document.addEventListener("fullscreenchange", updateFullscreenButton);
document.addEventListener("webkitfullscreenchange", updateFullscreenButton);
document.addEventListener("click", (event) => {
  const menu = document.querySelector(".file-menu-wrap");
  if (fileMenuOpen && menu && event.target instanceof Node && !menu.contains(event.target)) setFileMenuOpen(false);
});
window.addEventListener("keydown", (event) => {
  if (event.key === "Escape") setFileMenuOpen(false);
});
window.addEventListener("keydown", (event) => {
  if (event.code !== "Space" || document.activeElement !== canvas) return;
  event.preventDefault();
  spacePressed = true;
  updateViewport();
});
window.addEventListener("keyup", (event) => {
  if (event.code !== "Space") return;
  spacePressed = false;
  updateViewport();
});
window.addEventListener("blur", () => {
  spacePressed = false;
  if (isPanning) finishStroke();
  updateViewport();
});
const pageQrDialog = document.querySelector<HTMLDialogElement>("#page-qr-dialog")!;
document.querySelector("#open-page-qr")!.addEventListener("click", () => pageQrDialog.showModal());
pageQrDialog.addEventListener("click", () => pageQrDialog.close());
document.querySelectorAll<HTMLButtonElement>("[data-avatar-icon]").forEach((button) => {
  button.addEventListener("click", () => {
    if (isAvatarIcon(button.dataset.avatarIcon)) selectAvatarIcon(button.dataset.avatarIcon);
  });
});
document.querySelector("#save-profile")!.addEventListener("click", async () => {
  if (!authClient || !authSession) return;
  const button = document.querySelector<HTMLButtonElement>("#save-profile")!;
  const message = document.querySelector("#profile-message")!;
  button.disabled = true;
  message.textContent = "이름을 저장하는 중…";
  try {
    const displayName = (document.querySelector("#profile-name") as HTMLInputElement).value.trim();
    if (!displayName) throw new Error("표시 이름을 입력해 주세요.");
    const { profile } = await authClient.updateProfile(
      authSession.token,
      displayName,
      pendingAvatarIcon,
    );
    saveAuthSession({ token: authSession.token, profile });
    renderProfile();
    document.querySelector<HTMLDialogElement>("#profile-dialog")!.close();
  } catch (error) {
    message.textContent = error instanceof Error ? error.message : "이름을 변경하지 못했습니다.";
  } finally {
    button.disabled = false;
  }
});
document.querySelector("#logout")!.addEventListener("click", async () => {
  if (!authClient || !authSession) return;
  const button = document.querySelector<HTMLButtonElement>("#logout")!;
  const token = authSession.token;
  button.disabled = true;
  try {
    await authClient.logout(token);
  } catch (error) {
    console.error("Logout request failed", error);
  } finally {
    saveAuthSession(null);
    imageAssets = [];
    imageAssetsLoaded = false;
    imageAssetsById.clear();
    imageRenderImages.clear();
    imageMaterialTab = "default";
    selectedImageId = null;
    selectedImagePlacementIndex = null;
    renderImageMaterials();
    render();
    document.querySelector<HTMLDialogElement>("#profile-dialog")!.close();
    for (const id of ["#map-save-dialog", "#map-library-dialog"]) {
      const dialog = document.querySelector<HTMLDialogElement>(id);
      if (dialog?.open) dialog.close();
    }
    button.disabled = false;
    renderLoggedOut();
    void refreshAdminAccess();
  }
});
window.addEventListener("keydown", (event) => {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") { event.preventDefault(); event.shiftKey ? redo() : undo(); }
});
setBrushSize(brushSize);
setBrushMode(brushMode);
setPropMode(propMode);
setImageMode(imageMode);
syncImageTransformControls();
renderImageMaterials();
updateFullscreenButton();
updateViewport();
scheduleGroundNoiseBuild();
render();
if (isImageLibraryPage) {
  void initializeStandaloneImageLibraryPage();
} else {
  void initializeDeploymentTime();
  void initializeAuth();
}
